import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { translate } from "react-i18next";
import "./styles/bodymap.css";
import { Row, Col, Tooltip } from "reactstrap";
import bodyMapActions from "../actions/bodyMapActions";
import { Loading } from "../components";

const ANCHOR_Y_BAND = {
  /* 
    1. What it does: When the placement of tooltip is left or right, limits the Y range we search to a band inside the part.
    2. Why it matters: Keeps the algorithm away from irrelevant areas that would pull the tooltip to weird places.
    3. If you DON’T set it: Defaults to [0, 1] (full height).
    4. Example outcome (for frt_24_right):
     -- With [0.35, 0.65]: we only look around mid-arm → visually natural attachment.
     -- With [0, 1]: our scan line might sit at a Y that also crosses the hand; the edge on that line could be far out at the thumb.
  */

  FRONT_OF_RIGHT_UPPER_LIMB: [0.35, 0.65],
  FRONT_OF_LEFT_UPPER_LIMB: [0.35, 0.65],
  BACK_OF_RIGHT_UPPER_LIMB: [0.35, 0.65],
  BACK_OF_LEFT_UPPER_LIMB: [0.35, 0.65],
  RIGHT_OF_TONGUE: [0.35, 0.65],
  LEFT_OF_TONGUE: [0.55, 0.75],
  UNDER_EYEBROW: [0.85, 0.95],  // (right and left)
  FRONT_OF_WRIST: [0.15, 0.25],  // (right and left)
  BACK_OF_WRIST: [0.15, 0.25],  // (right and left)
  ELBOW_PIT: [0.15, 0.45], 
  FRONT_OF_LOWER_ARM: [0.35, 0.45],
  ELBOW: [0.15, 0.45],
  PALM: [0.55, 0.75], 
  PALM_SIDE_OF_PINKY: [0, 0.1],
  BACK_OF_PINKY: [0, 0.1],
  PALM_SIDE_OF_THUMB: [0.1, 0.2],
  BACK_OF_THUMB: [0.1, 0.2],
};

const ANCHOR_X_CLIP = {
  /* 
    1. What it does: When the placement of tooltip is left or right, crops the X range closer to the body so the scan ignores the far side (hand).
    2. Why it matters: Even if your band is good, the strict horizontal line can still intersect the hand at the same Y. Clip prevents the hand from “winning.”
    3. If you DON’T set it: Defaults to 1 (no crop). On the chosen Y, we’ll consider the full width; if the hand sticks farther right, it will be chosen as the “nearest edge,” pushing the bubble outward.
    4. Example outcome (for frt_24_right):
     -- With 0.58: we only consider the left 58% of the limb bbox (the arm near the torso). The edge we pick is on the upper arm.
     With 1.0: the hand can still be considered; if it extends further at that Y, it drags the bubble to the right.
  */

  // "FRONT_OF_RIGHT_UPPER_LIMB_right": 0.58,   // Front Right Upper Limb → use left 58% (ignore hand)
  // "FRONT_OF_LEFT_UPPER_LIMB_left":  0.58,   // Front Left  Upper Limb → use right 58%
  // "BACK_OF_RIGHT_UPPER_LIMB_right": 0.58,   // Back  Right Upper Limb
  // "BACK_OF_LEFT_UPPER_LIMB_left":  0.58,   // Back  Left  Upper Limb
};


// For TOP/BOTTOM placement:
// Limit the horizontal search window to an X-band of the bbox.
// 0 = left edge, 1 = right edge. If not set → [0, 1].
const ANCHOR_X_BAND = {
  /* 
    1. What it does: When the placement of tooltip is top or bottom, limits the X range we search to a band inside the part.
    2. Why it matters: Keeps the algorithm away from irrelevant areas that would pull the tooltip to weird places.
    3. If you DON’T set it: Defaults to [0, 1] (full width).
    4. Example outcome (for frt_24_right):
     -- With [0.35, 0.65]: we only look around mid-arm → visually natural attachment.
     -- With [0, 1]: our scan line might sit at a Y that also crosses the hand; the edge on that line could be far out at the thumb.
  */

  PALM_SIDE_OF_LEFT_RING_FINGER: [0.8, 0.9],
  BACK_OF_LEFT_RING_FINGER: [0, 0.1],
  PALM_SIDE_OF_LEFT_MIDDLE_FINGER: [0.7, 0.8],
  BACK_OF_LEFT_MIDDLE_FINGER: [0.1, 0.2],
  PALM_SIDE_OF_LEFT_INDEX_FINGER: [0.2, 0.3],
  BACK_OF_LEFT_INDEX_FINGER: [0.6, 0.65],

  PALM_SIDE_OF_RIGHT_RING_FINGER: [0.2, 0.3],
  BACK_OF_RIGHT_RING_FINGER: [0.8, 0.9],
  PALM_SIDE_OF_RIGHT_MIDDLE_FINGER: [0.2, 0.3],
  BACK_OF_RIGHT_MIDDLE_FINGER: [0.7, 0.8],
  PALM_SIDE_OF_RIGHT_INDEX_FINGER: [0.7, 0.8],
  BACK_OF_RIGHT_INDEX_FINGER: [0.2, 0.3],

};

// Keep only a percentage of height toward the side of the tooltip.
// For TOP: keep the TOP clipPct of the bbox.
// For BOTTOM: keep the BOTTOM clipPct of the bbox.
// If not set → 1 (no vertical clipping).
const ANCHOR_Y_CLIP = {
  /* 
    1. What it does: When the placement of tooltip is top or bottom, crops the Y range closer to the body so the scan ignores the far side.
    2. Why it matters: Even if your band is good, the strict horizontal line can still intersect the hand at the same X. Clip prevents the hand from “winning.”
    3. If you DON’T set it: Defaults to 1 (no crop). On the chosen X, we’ll consider the full width; if the hand sticks farther right, it will be chosen as the “nearest edge,” pushing the bubble outward.
    4. Example outcome (for frt_24_right):
     -- With 0.58: we only consider the left 58% of the limb bbox (the arm near the torso). The edge we pick is on the upper arm.
     With 1.0: the hand can still be considered; if it extends further at that X, it drags the bubble to the right.
  */

  // `${id}_top`: 0.60,   // keep top 60% of height
  // `${id}_bottom`: 0.55 // keep bottom 55% of height
};


const SHOW_DEBUG_OVERLAY = false; // set false to hide without deleting
const DEBUG_SHOW_BBOX = false; // set true if you want the purple outline
class BodyMap extends React.Component {
  svgRef = React.createRef();
  constructor(props) {
    super(props);
    this.state = {
      body: "front",
      bodyBack: "front",
      form: false,
      bodyPart: "",
      selectedLocation: "",
      bodySymptomList: [],
      selectedSymptom: {},
      tooltipOpen: "9999",
      title: "",
      isChrome: false,
      isFirefox: false,
    };
  }

  // Return center of the map (SVG) we render into
  getMapRect = () => {
    const svg = this.svgRef && this.svgRef.current;
    return (svg ? svg.getBoundingClientRect() : document.body.getBoundingClientRect());
  };

  // Strict-axis edge finder using only ANCHOR_Y_BAND + ANCHOR_X_CLIP
  axisEdgeAnchor = (id, side) => {
    this._dbg = null;
    this._pokeDebugRender();
    const el  = document.getElementById(id);
    const svg = this.svgRef && this.svgRef.current;
    if (!el || !svg) return null;

    const r = el.getBoundingClientRect();
    const isPath =
      typeof el.getTotalLength === "function" &&
      typeof el.getPointAtLength === "function" &&
      typeof el.getScreenCTM === "function";

    // Fallback for non-path parts
    if (!isPath) {
      const cx = (r.left + r.right) / 2;
      const cy = (r.top  + r.bottom) / 2;
      if (side === "left")  return { x: r.left,  y: cy };
      if (side === "right") return { x: r.right, y: cy };
      if (side === "top")   return { x: cx,      y: r.top };
      return { x: cx,        y: r.bottom };
    }

    const m   = el.getScreenCTM();
    const pt  = svg.createSVGPoint();
    const len = el.getTotalLength();
    const SAMPLES = 360;

    // ----- LEFT/RIGHT: strict horizontal scan line inside a vertical band -----
    if (side === "left" || side === "right") {
      // 1) band (limit Y)
      const [b0, b1] = (ANCHOR_Y_BAND[id] || [0, 1]);
      const yMin = r.top  + Math.min(b0, b1) * r.height;
      const yMax = r.top  + Math.max(b0, b1) * r.height;
      const targetY = (yMin + yMax) / 2;  // band center

      // 2) horizontal clip (limit X to proximal portion only)
      const clipPct = (ANCHOR_X_CLIP && ANCHOR_X_CLIP[`${id}_${side}`]) || 1;
      let xMin = r.left, xMax = r.right;
      if (clipPct < 1) {
        if (side === "right") xMax = r.left  + r.width * clipPct; // keep LEFT portion
        else                  xMin = r.right - r.width * clipPct; // keep RIGHT portion
      }

      // 3) compute the true intersection on that strict line
      const m   = el.getScreenCTM();
      const len = el.getTotalLength();
      let chosen = this.pickEdgeAtYStrict(el, svg, m, len, targetY, xMin, xMax, side, yMin, yMax);

      // 4) fallback if the line doesn't cross inside the window
      if (!chosen) {
        // Use the window boundary on the correct side, at targetY
        chosen = { x: side === "right" ? xMax : xMin, y: targetY };
      }

      const x = chosen.x;
      const y = chosen.y;

      // (optional) stash debug snapshot so your overlay shows the exact values
      // this._dbg = {
      //   id, side, xMin, xMax, yMin, yMax,
      //   clipLineX: side === "right" ? xMax : xMin,
      //   anchor: { x, y }
      // };
      this._dbg = {
        id,
        side, // "left" or "right"
        bbox: { x: r.left, y: r.top, w: r.width, h: r.height },

        // Orange "band" lines (top/bottom) – we draw them across the entire bbox width
        band: {
          xMin: r.left,
          xMax: r.right,
          yMin,       // top of the vertical band you computed from ANCHOR_BAND
          yMax        // bottom of the vertical band
        },

        // Thin orange "strict sampling" line
        axis: { targetY },   // the exact Y we sample at

        // Cyan vertical clip boundary (if you used a clipPct < 1)
        clipLineX: side === "right" ? xMax : xMin,

        // Red dot
        edge:   { x, y },    // (optional, same as anchor)
        anchor: { x, y }
      };

      this._pokeDebugRender();

      return { x, y };

    }

    // ----- TOP/BOTTOM: strict vertical scan line inside an X-band + Y-clip -----
    if (side === "top" || side === "bottom") {
      const r = el.getBoundingClientRect();
      const m   = el.getScreenCTM();
      const len = el.getTotalLength();

      // 1) X-band (limit horizontal window)
      const xb = (ANCHOR_X_BAND[id] || [0, 1]);
      const bx0 = Math.min(xb[0], xb[1]);
      const bx1 = Math.max(xb[0], xb[1]);
      const xMinBand = r.left + bx0 * r.width;
      const xMaxBand = r.left + bx1 * r.width;
      const centerX = (xMinBand + xMaxBand) / 2;

      // 2) Y-clip (keep portion toward the side)
      const clipPct = (ANCHOR_Y_CLIP && ANCHOR_Y_CLIP[`${id}_${side}`]) || 1;
      let yMin = r.top, yMax = r.bottom;
      if (clipPct < 1) {
        if (side === "top") {
          yMax = r.top + r.height * clipPct;         // keep TOP portion
        } else {
          yMin = r.bottom - r.height * clipPct;      // keep BOTTOM portion
        }
      }

      // 3) Intersection along x = centerX within [yMin, yMax]
      let chosen = this.pickEdgeAtXStrict(el, svg, m, len, centerX, yMin, yMax, side);

      // 4) Fallback if no crossing in window: stick to boundary line
      if (!chosen) chosen = { x: centerX, y: side === "top" ? yMin : yMax };

      const x = chosen.x;
      const y = chosen.y;

      // --- Debug snapshot (so the overlay can draw lines & dot) ---
      this._dbg = {
        id, side,
        bbox: { x: r.left, y: r.top, w: r.width, h: r.height },
        xBand: { xMin: xMinBand, xMax: xMaxBand, yMin, yMax },
        axis:  { centerX },
        clipLineY: side === "top" ? yMax : yMin,  // cyan horizontal line
        edge: { x, y },
        anchor: { x, y }
      };
      this._pokeDebugRender && this._pokeDebugRender();

      return { x, y };
    }

    // ----- SAFETY: strict vertical scan line through center X -----
    const centerX = (r.left + r.right) / 2;
    let bestY = side === "bottom" ? -Infinity : Infinity;

    for (let i = 0; i <= SAMPLES; i++) {
      const p = el.getPointAtLength((i / SAMPLES) * len);
      pt.x = p.x; pt.y = p.y;
      const s = pt.matrixTransform(m);
      if (Math.abs(s.x - centerX) > 2) continue;      // strict vertical line
      if (side === "bottom" ? s.y > bestY : s.y < bestY) bestY = s.y;
    }
    if (!isFinite(bestY)) bestY = side === "bottom" ? r.bottom : r.top;

    return { x: centerX, y: bestY };
  };

  // Returns the extreme intersection point of the path with the horizontal
  // line y = targetY, constrained to [xMin, xMax]. Picks max X for "right",
  // min X for "left". Falls back to null if no crossing inside the window.
  pickEdgeAtYStrict(el, svg, m, len, targetY, xMin, xMax, side, yMin, yMax) {
    const pt = svg.createSVGPoint();
    const SAMPLES = 720;                    // denser = smoother intersections
    const step = len / SAMPLES;

    let chosen = null;

    // Walk consecutive pairs (segments)
    let prev = el.getPointAtLength(0);
    pt.x = prev.x; pt.y = prev.y;
    let sPrev = pt.matrixTransform(m);

    for (let i = 1; i <= SAMPLES; i++) {
      const cur = el.getPointAtLength(i * step);
      pt.x = cur.x; pt.y = cur.y;
      const sCur = pt.matrixTransform(m);

      // Keep only segments that overlap the vertical band
      const segYmin = Math.min(sPrev.y, sCur.y);
      const segYmax = Math.max(sPrev.y, sCur.y);
      if (segYmax < yMin || segYmin > yMax) { sPrev = sCur; continue; }

      // Does this segment cross y = targetY?
      const d0 = sPrev.y - targetY;
      const d1 = sCur.y  - targetY;
      if ((d0 === 0 && d1 === 0)) { sPrev = sCur; continue; } // colinear: ignore
      if ((d0 <= 0 && d1 >= 0) || (d0 >= 0 && d1 <= 0)) {
        // Linear interpolation for X at targetY
        const denom = (sCur.y - sPrev.y);
        if (Math.abs(denom) > 1e-6) {
          const t = (targetY - sPrev.y) / denom;
          const x = sPrev.x + t * (sCur.x - sPrev.x);

          // Only accept intersections inside the clip window (+tiny slack)
          if (x >= xMin - 0.5 && x <= xMax + 0.5) {
            const hit = { x, y: targetY };
            if (!chosen ||
                (side === "right" ? hit.x > chosen.x : hit.x < chosen.x)) {
              chosen = hit;
            }
          }
        }
      }

      sPrev = sCur;
    }

    return chosen; // null if no crossing inside [xMin,xMax]
  }

  // Exact intersection of the path with the vertical line x = targetX,
  // constrained to y ∈ [yMin, yMax]. For 'top' pick smallest y; for 'bottom' pick largest y.
  pickEdgeAtXStrict(el, svg, m, len, targetX, yMin, yMax, side) {
    const pt = svg.createSVGPoint();
    const SAMPLES = 720;
    const step = len / SAMPLES;

    let chosen = null;

    let prev = el.getPointAtLength(0);
    pt.x = prev.x; pt.y = prev.y;
    let sPrev = pt.matrixTransform(m);

    for (let i = 1; i <= SAMPLES; i++) {
      const cur = el.getPointAtLength(i * step);
      pt.x = cur.x; pt.y = cur.y;
      const sCur = pt.matrixTransform(m);

      // Segment must span targetX
      const x0 = sPrev.x, x1 = sCur.x;
      const crosses = (x0 <= targetX && x1 >= targetX) || (x0 >= targetX && x1 <= targetX);
      if (!crosses) { sPrev = sCur; continue; }

      // Interpolate y at targetX
      const dx = (x1 - x0);
      if (Math.abs(dx) < 1e-6) { sPrev = sCur; continue; } // vertical segment, skip
      const t = (targetX - x0) / dx;
      const y = sPrev.y + t * (sCur.y - sPrev.y);

      // Respect the vertical clip window
      if (y < yMin - 0.5 || y > yMax + 0.5) { sPrev = sCur; continue; }

      const hit = { x: targetX, y };
      if (!chosen) {
        chosen = hit;
      } else if (side === "top") {
        if (hit.y < chosen.y) chosen = hit;
      } else { // bottom
        if (hit.y > chosen.y) chosen = hit;
      }

      sPrev = sCur;
    }

    return chosen; // null if no crossing in window
  }


  renderDebugOverlay() {
    if (!SHOW_DEBUG_OVERLAY || !this._dbg) return null;

    const d = this._dbg;
    const side = String(d.side || "").toLowerCase();

    const lineStyle = (left, top, width, height, color) => ({
      position: "fixed",
      left, top, width, height,
      background: color,
      pointerEvents: "none",
    });
    const dotStyle = (left, top, color) => ({
      position: "fixed",
      left, top, width: 10, height: 10,
      marginLeft: -5, marginTop: -5,
      borderRadius: "50%",
      background: color,
      boxShadow: "0 0 0 2px rgba(0,0,0,0.15)",
      pointerEvents: "none",
    });

    const items = [];

    // Safe bbox fallback
    const bbox = d.bbox || { x: 0, y: 0, w: 0, h: 0 };

    // Optional: faint bbox outline
    if (DEBUG_SHOW_BBOX && d.bbox) {
      items.push(
        <div
          key="bbox"
          style={{
            position: "fixed",
            left: bbox.x,
            top: bbox.y,
            width: bbox.w,
            height: bbox.h,
            outline: "2px solid #b100ff",
            background: "rgba(177,0,255,0.05)",
            pointerEvents: "none",
          }}
        />
      );
    }

    if (side === "left" || side === "right") {
      // ----- LEFT / RIGHT -----
      const band = d.band || {};

      const bx = Number.isFinite(band.xMin) ? band.xMin : bbox.x;
      const bx2 =
        Number.isFinite(band.xMax) ? band.xMax : (bbox.x + bbox.w);
      const by = Number.isFinite(band.yMin) ? band.yMin : bbox.y;
      const by2 =
        Number.isFinite(band.yMax) ? band.yMax : (bbox.y + bbox.h);
      const bw = Math.max(0, bx2 - bx);

      // Orange band edges (horizontal)
      items.push(<div key="band-top" style={lineStyle(bx, by, bw, 2, "orange")} />);
      items.push(<div key="band-bot" style={lineStyle(bx, by2, bw, 2, "orange")} />);

      // Strict horizontal sampling line (thin orange)
      if (d.axis && Number.isFinite(d.axis.targetY)) {
        items.push(
          <div
            key="strict-h"
            style={lineStyle(bbox.x, d.axis.targetY, bbox.w, 1, "orange")}
          />
        );
      }

      // Cyan vertical clip boundary spanning the band
      if (Number.isFinite(d.clipLineX)) {
        const yTop = Math.min(by, by2);
        const yHt = Math.abs(by2 - by);
        items.push(
          <div
            key="clip-x"
            style={lineStyle(d.clipLineX, yTop, 2, yHt, "#00e5ff")}
          />
        );
      }
    } else if (side === "top" || side === "bottom") {
      // ----- TOP / BOTTOM -----
      const xb = d.xBand || {};
      const xL = Number.isFinite(xb.xMin) ? xb.xMin : bbox.x;
      const xR =
        Number.isFinite(xb.xMax) ? xb.xMax : (bbox.x + bbox.w);
      const yT = Number.isFinite(xb.yMin) ? xb.yMin : bbox.y;
      const yB =
        Number.isFinite(xb.yMax) ? xb.yMax : (bbox.y + bbox.h);

      // Orange X-band edges (vertical)
      items.push(<div key="xband-left"  style={lineStyle(xL, yT, 2, Math.max(0, yB - yT), "orange")} />);
      items.push(<div key="xband-right" style={lineStyle(xR, yT, 2, Math.max(0, yB - yT), "orange")} />);

      // Strict vertical sampling line (thin orange)
      if (d.axis && Number.isFinite(d.axis.centerX)) {
        items.push(
          <div
            key="strict-v"
            style={lineStyle(d.axis.centerX, bbox.y, 1, bbox.h, "orange")}
          />
        );
      }

      // Cyan horizontal clip boundary
      if (Number.isFinite(d.clipLineY)) {
        items.push(
          <div
            key="clip-y"
            style={lineStyle(bbox.x, d.clipLineY, bbox.w, 2, "#00e5ff")}
          />
        );
      }
    }

    // Red anchor dot
    if (d.anchor && Number.isFinite(d.anchor.x) && Number.isFinite(d.anchor.y)) {
      items.push(<div key="anchor" style={dotStyle(d.anchor.x, d.anchor.y, "#ff2d2d")} />);
    }

    return <div className="bm-debug-layer">{items}</div>;
  }

  // Re-render once on the next animation frame (cheap + throttled)
  _dbgRaf = 0;
  _pokeDebugRender = () => {
    if (!SHOW_DEBUG_OVERLAY) return;
    if (this._dbgRaf) cancelAnimationFrame(this._dbgRaf);
    this._dbgRaf = requestAnimationFrame(() => {
      this._dbgRaf = 0;
      this.forceUpdate();
    });
  };



  componentDidMount() {
    this.props.bodyInitialization();
    const userAgent = navigator.userAgent;

    // Check if the user agent string contains "Chrome"
    if (userAgent.indexOf("Chrome") !== -1) {
      this.setState({ isChrome: true });
    }
    // Check if the user agent string contains "Firefox"
    else if (userAgent.indexOf("Firefox") !== -1) {
      this.setState({ isFirefox: true });
    }
  }

  async locationClicked(id) {
    this.setState({
      selectedLocation: id,
      bodySymptomList: this.props.bodySymptoms[id],
      selectedSymptom: {},
    });
  }
  symptomSelected(item) {
    this.setState({ selectedSymptom: item });
  }
  toggle = (id) => {
    // if (this.state.tooltipOpen === "9999") {
    //   id = id;
    // } else {
    //   id = "9999";
    // }
    // this.setState({ tooltipOpen: id });

    this.setState(({ tooltipOpen }) => ({
      tooltipOpen: tooltipOpen === id ? "9999" : id,
    }));
  };
  toolTip(id, name, placement) {
    const ARROW_PX = 6;   // matches CSS triangle size

    return (
      !!document.getElementById(id) && (
        <Tooltip
          key={this.state.TooltipID} 
          container="body"
          delay={100}
          fade={false}
          placement={placement}
          isOpen={this.state.tooltipOpen === id}
          target={id}
          popperClassName="custom-tooltip"
          toggle={() => this.toggle(id)}
          style={{
            textAlign: "center",
            fontSize: "0.8rem",
          }}
          modifiers={{
            // Put the popper box exactly on our strict-axis anchor
            lockToAnchor: {
              enabled: true,
              order: 840, // just before computeStyle (850)
              fn: (data) => {
                const side = data.placement.split('-')[0];
                const anchor = this.axisEdgeAnchor(id, side);
                if (!anchor) return data;

                // Convert anchor (viewport) -> popper offsetParent space
                const popEl = data.instance.popper;
                const offParent = popEl.offsetParent || document.body;
                const parentRect = offParent.getBoundingClientRect();
                const ax = anchor.x - parentRect.left;
                const ay = anchor.y - parentRect.top;

                const p = data.offsets.popper;      // current box
                const w = p.width;
                const h = p.height;

                // Arrow width already overlaps the box in CSS, so gap = 0
                const GAP = ARROW_PX;

                if (side === 'right') {
                  p.left = ax + GAP;         // box touches anchor at its left edge
                  p.top  = ay - h / 2 - 12;       // stay vertically centered
                } else if (side === 'left') {
                  p.left = ax - w - GAP;     // box touches anchor at its right edge
                  p.top  = ay - h / 2 - 12;
                } else if (side === 'top') {
                  p.left = ax - w / 2;       // horizontally centered
                  p.top  = ay - h - GAP - 30;
                } else { // bottom
                  p.left = ax - w / 2;
                  p.top  = ay + GAP;
                }

                data.offsets.popper = p;
                data.placement = side;       // normalize "right-start" → "right"
                return data;
              },
            },

            arrow: { enabled: false }, // we draw our own arrow
            flip: { enabled: true },              // allow flip near edges
            preventOverflow: { boundariesElement: "viewport", padding: 8 },
          }}
        >
          {name}
          <span className="custom-tooltip-arrow" aria-hidden="true"></span>
        </Tooltip>
      )
    );
  }
  changeView(part) {
    let bodyBack = this.state.body;
    if (
      part === "head" ||
      part === "frontRightUpperLimb" ||
      part === "frontLeftUpperLimb" ||
      part === "frontLeftLowerLimb" ||
      part === "frontRightLowerLimb"
    ) {
      bodyBack = "front";
    } else if (
      part === "bHead" ||
      part === "backLeftUpperLimb" ||
      part === "backRightUpperLimb" ||
      part === "backLeftLowerLimb" ||
      part === "backRightLowerLimb"
    ) {
      bodyBack = "back";
    } else if (part === "fRightFoot") {
      bodyBack = "frontRightLowerLimb";
    } else if (part === "fLeftFoot") {
      bodyBack = "frontLeftLowerLimb";
    } else if (part === "bRightFoot") {
      bodyBack = "backRightLowerLimb";
    } else if (part === "bLeftFoot") {
      bodyBack = "backLeftLowerLimb";
    } else if (part === "fRightHand") {
      bodyBack = "frontRightUpperLimb";
    } else if (part === "fLeftHand") {
      bodyBack = "frontLeftUpperLimb";
    } else if (part === "bRightHand") {
      bodyBack = "backRightUpperLimb";
    } else if (part === "bLeftHand") {
      bodyBack = "backLeftUpperLimb";
    } else if (part === "mouth") {
      bodyBack = "head";
    }

    this.setState({ bodyBack, body: part, tooltipOpen: "9999" });
    setTimeout(() => this.toggle("9999"), 100);
  }
  onBodyClick(id, bTitle) {
    this.setState({ form: true, bodyPart: id, title: bTitle });
    this.props.getBodyParts(id);
  }
  doneClicked(item) {
    let location = this.state.selectedLocation
      ? this.state.selectedLocation
      : false;
    this.props.doneClicked(this.state.bodyPart, item._id, location);
  }
  bodyForm() {
    return (
      <div>
        <div style={{ marginTop: "-10px" }}>
          <Row>
            <p
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                marginBottom: "0px",
                marginLeft: "12px",
              }}
            >
              {this.state.title}
            </p>
          </Row>
          <Row style={{ marginLeft: "-10px" }}>
            {this.props.bodySymptoms &&
              this.props.bodySymptoms.locations &&
              this.props.bodySymptoms.locations.length > 0 &&
              this.props.bodySymptoms.locations.map((location, index) => {
                return (
                  <div key={index} style={{ marginLeft: "20px" }}>
                    <div className="item">
                      <label>
                        <input
                          type="radio"
                          checked={
                            this.state.selectedLocation &&
                              this.state.selectedLocation == location._id
                              ? true
                              : false
                          }
                          onChange={() => {
                            this.locationClicked(location._id);
                          }}
                          style={{
                            marginRight: "8px",
                            cursor: "pointer",
                          }}
                        />
                        {location.name}
                        <span
                          style={{
                            backgroundColor: "#fff",
                          }}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
          </Row>
          <Row>
            <p
              style={{
                fontWeight: "bold",
                marginBottom: "0.5rem",
                marginLeft: "12px",
              }}
            >
              {this.props.t("general.CATEGORY_SYMPTOM")}
            </p>
          </Row>
        </div>
        <div
          className={`grayList ingredient-list `}
          style={{ height: "43vh", marginBottom: "0.3rem" }}
        >
          {this.props.bodySymptoms.locations &&
            this.props.bodySymptoms.locations.length > 0 &&
            !this.state.selectedLocation && (
              <div className="usersItem">
                <Row style={{ padding: "4px 0 4px 4px" }}>
                  <Col>
                    <span className="desc">
                      {this.props.t("bodyMap.SELECT_LOCATION_TEXT")}
                    </span>
                  </Col>
                </Row>
              </div>
            )}
          {this.props.bodySymptoms && (
            <div>
              {this.state.bodySymptomList &&
                this.state.bodySymptomList.map((item, index) => {
                  return (
                    <div className="usersItem">
                      <Row
                        className="wrapper"
                        style={{
                          backgroundColor:
                            this.state.selectedSymptom._id == item._id
                              ? "#718afa1a"
                              : "#fff",
                        }}
                      >
                        <Col
                          onClick={() => this.symptomSelected(item)}
                          key={index}
                          style={{
                            flex: 1,
                            display: "flex",
                            paddingRight: 4,
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <span className="desc">{item.name}</span>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
            </div>
          )}
          {this.props.bodySymptoms && !this.props.bodySymptoms.locations && (
            <div>
              {this.props.bodySymptoms &&
                this.props.bodySymptoms["20000012153"] &&
                this.props.bodySymptoms["20000012153"].map((item, index) => {
                  return (
                    <div className="usersItem">
                      <Row
                        className="wrapper"
                        style={{
                          backgroundColor:
                            this.state.selectedSymptom._id == item._id
                              ? "#718afa1a"
                              : "#fff",
                        }}
                      >
                        <Col
                          onClick={() => this.symptomSelected(item)}
                          key={index}
                        >
                          <span className="desc">{item.name}</span>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
          {this.props.t("support.DESCRIPTION_TEXT")}
        </span>
        <div
          className={`grayList ingredient-list `}
          style={{ flex: 1, marginTop: "0.3rem" }}
        >
          {this.state.selectedSymptom && this.state.selectedSymptom.brief ? (
            <div>
              <Row className="wrapper">
                <Col>
                  <div
                    style={{ flexDirection: "row", flex: 1, display: "flex" }}
                  >
                    {this.state.selectedSymptom.url && (
                      <img
                        src={this.state.selectedSymptom.url}
                        style={{ height: 150, marginRight: 12 }}
                      />
                    )}
                    <p className="desc">{this.state.selectedSymptom.brief}</p>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <Row
                className="wrapper"
                style={{
                  padding: "0px 0px 4px 4px",
                }}
              >
                <Col>
                  <span className="usersItem desc">
                    {
                      this.props.bodySymptoms.locations &&
                        this.props.bodySymptoms.locations.length > 0 &&
                        this.state.selectedLocation
                        ? this.props.t("bodyMap.SELECT_SYMPTOM_BODYMAP")
                        : !this.props.bodySymptoms.locations &&
                          !this.state.selectedLocation
                          ? this.props.t("bodyMap.SELECT_SYMPTOM_BODYMAP")
                          : this.props.t("bodyMap.SELECT_LOCATION_TEXT")
                    }
                  </span>
                </Col>
              </Row>
            </div>
          )}
        </div>
        <Row>
          <Col>
            <button
              type="button"
              className="btn btn-primary btnWide"
              onClick={() => {
                this.setState({
                  form: false,
                  selectedLocation: "",
                  selectedSymptom: {},
                  bodySymptomList: [],
                  tooltipOpen: "9999",
                });
                setTimeout(() => {
                  this.setState({ tooltipOpen: "9999" });
                  this.toggle("9999");
                }, 100);
              }}
            >
              {this.props.t("bodyMap.BACK_BUTTON")}
            </button>
          </Col>
          <Col>
            <button
              onClick={() => {
                localStorage.setItem("bodyMapSymptom", "cancel");
                let returnPage =
                  localStorage.getItem("bodyreturn") || "symptom";
                sessionStorage.removeItem("previousPageIsDirty"); // ✅ Clear session flag
                window.location = "/" + returnPage;
              }}
              className="btn btn-primary btnWide"
            >
              {this.props.t("general.CANCEL")}
            </button>
          </Col>
          <Col>
            <button
              type="button"
              className="btn btn-primary btnWide"
              onClick={() => {
                sessionStorage.removeItem("previousPageIsDirty"); // ✅ Clear session flag
                this.doneClicked(this.state.selectedSymptom);
              }}
              disabled={
                this.state.selectedSymptom && this.state.selectedSymptom._id
                  ? false
                  : true
              }
            >
              {this.props.t("general.DONE_BUTTON")}
            </button>
          </Col>
        </Row>
      </div>
    );
  }

  bodyFront() {
    return (
      <div style={{}}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("back")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "10rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>

        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "28rem",
            left: "2rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "23.5rem",
            width: "50px",
            left: "16.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div style={{}}>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="800px"
            height="100%"
            viewBox="0 0 800 1360"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x="39" // Set your x-coordinate value here
              y="0"
              height="1367"
              xlinkHref={require("../images/Body-Front v2.png")}
            ></image>
            {/* </div> */}

            <path
              onClick={() => this.changeView("head")}
              id="FRONT_OF_HEAD_TEXT"
              d="M450,143c15,16,29-52,6-34c12-72-35.2-89.7-53.2-90.7c-29.3,1.4-51,30.2-54,43.2c-1.9,8.3-2.8,23.5-2.5,28.2
	s1,12.3,0.7,19.3c-23-19-6,51,6.7,33c0.7,5.2,1.2,12.5,3.3,18.8c1,12.2,1.3,39-0.7,45c-2,3.7-9.5,10.1-17.7,15
	c-12,7,38.7,32.5,63.4,32.2c26.2-0.3,75.6-26.6,63.1-32.1c-21.9-14-21.1-8.8-19.1-53.8L450,143z"
            />
            {this.toolTip(
              "FRONT_OF_HEAD_TEXT",
              this.props.t("bodyMap.FRONT_OF_HEAD_TEXT"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012126",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_SHOULDER")
                )
              }
              id="FRONT_OF_RIGHT_SHOULDER"
              d="M546.9,247c0,0-23.2-10.3-42.8-12.4c-19.6-2.1-38.5-12.7-38.5-12.7s4.1,6.5-17.1,16.5
	c-21.2,10-35.8,14.2-44.6,14.4c0,0,26.9,1.2,43.4,3.2c16.5,2,48.6,10.2,74.5,23.2C521.9,279.2,539.4,249.9,546.9,247z"
            />
            {this.toolTip(
              "FRONT_OF_RIGHT_SHOULDER",
              this.props.t("bodyMap.FRONT_OF_RIGHT_SHOULDER"),
              "right"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000018371",
                  this.props.t("bodyMap.FRONT_OF_LEFT_SHOULDER")
                )
              }
              id="FRONT_OF_LEFT_SHOULDER"
              d="M256,247c0,0,23.2-10.3,42.8-12.4c19.6-2.1,38.5-12.7,38.5-12.7s-4.1,6.5,17.1,16.5
	c21.2,10,35.8,14.2,44.6,14.4c0,0-26.9,1.2-43.4,3.2c-16.5,2-48.6,10.2-74.5,23.2C281,279.2,263.5,249.9,256,247z"
            />
            {this.toolTip(
              "FRONT_OF_LEFT_SHOULDER",
              this.props.t("bodyMap.FRONT_OF_LEFT_SHOULDER"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012100",
                  this.props.t("bodyMap.CHEST_TEXT")
                )
              }
              id="CHEST_TEXT"
              d="M521.9,279.2c-41.6-19.7-81.4-26.1-122-26.3c-39-0.2-78.6,6.9-119,25.7c0,0-3,102.5-3.2,116.1
	c0.4-0.1,9.7,25.1,12.2,30.8s3.7,15.5,7.2,22.2c8.2,7.8,46.8,25.2,73.2,18.2s36.5-6.2,65,0.1s52.7-2.8,73.1-27.2
	c1.2-3.2,4.8-11.8,5.3-15s2.7-7,4.1-9.7s7.5-21.7,8-21.2C526.7,379.6,521.9,279.2,521.9,279.2z"
            />
            {this.toolTip(
              "CHEST_TEXT",
              this.props.t("bodyMap.CHEST_TEXT"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012049",
                  this.props.t("bodyMap.ABDOMEN_TEXT")
                )
              }
              id="ABDOMEN_TEXT"
              d="M435.5,466.128C407,459.756,397,459,370.5,466s-65-10.5-73.25-18.25c3.5,6.75,2,12,3.75,17.75s5,21.334,0.5,41.501s-1.667,35.666-0.5,40.166c0.785,3.029,2.326,5.001,1.419,8.813C314,567.5,332.834,590.5,402.917,590.5s86.417-20.498,98.75-33.499c-1.666-4.5-0.501-12,2.499-21.167s-3.499-44.667-3.833-52.833s2.501-21.5,2.751-27.584s4.25-13.25,5.5-16.5C488.168,463.334,464,472.5,435.5,466.128z"
            />
            {this.toolTip(
              "ABDOMEN_TEXT",
              this.props.t("bodyMap.ABDOMEN_TEXT"),
              "bottom"
            )}

            <path
              onClick={() => this.changeView("pelvis")}
              id="PELVIS_TEXT"
              d="M 402.917 590.5 c -70.083 0 -88.917 -23 -100.498 -34.52 c -0.44 1.852 -1.458 4.137 -3.419 7.188 c -2.708 4.214 -5.009 15.491 -6.673 27.332 c 10.34 9.027 56.21 47.939 84.084 82.636 c 26.589 29.864 24.589 34.864 49.606 -0.453 c 28.214 -33.03 74.964 -71.046 85.649 -79.515 c -1 -13.666 -8.334 -31.667 -10 -36.167 C 489.334 570.002 473 590.5 402.917 590.5 z"
            />
            {this.toolTip(
              "PELVIS_TEXT",
              this.props.t("bodyMap.PELVIS_TEXT"),
              "bottom"
            )}
            <path
              onClick={() => this.changeView("frontLeftUpperLimb")}
              id="FRONT_OF_LEFT_UPPER_LIMB"
              d="M160.8,633.2c0.1-1,0.3-2.2,0.7-3.5C179,579,221,550,240,490c3.7-13.7,5.3-26.3,7-37l29-58l5-114
	c-7.3-14.6-15.3-26.9-25-34c0,0-24.2,14.5-30,34c-8,22-6.3,53.7-7,67c-6,9-23,41-28,71c-32,43-41,118-55,158l-17,34
	c-1.9,1.6-5.3,3-12.8,5.1c-1.9,0.5-3.8,1.3-5.9,2.2c-12.7,5.8-27.9,18.4-31.9,21c-4.6,3-7.8,8.4-11.9,10.5s-4.1,8.6,0,10.5
	s9.6,0.1,13-1.5s9-8.5,15.5-10.5c3.8-1.2,7.6-1.5,7.6,0.1s-8.5,22.4-9.1,25.5s-3.9,13.9-5.9,21.1s-5.5,21.2-6.8,29.2
	s0.9,11.8,5.1,12.6s7.9-7.6,8.6-10.6s2.9-12.8,4-15.5s6.6-18.8,8-22s2.4-8.6,4.4-7.8s-0.4,5.9-1.8,9.8s-7.6,24-8.4,27.9
	s-5,19.8-5.2,22.5s-1.9,8.8,2.8,10.5s7.8-1.9,9.5-5.6s5.4-17.6,7.4-26.1s5.8-19.5,7.1-24s2.1-8,3.9-7.9s1.5,2.5,0.8,4.8
	s-6.1,20.6-7.1,25.6s-4.2,16.1-5.4,20.4s-1.8,9.2,2.5,10.8s6.9-1.5,8.8-4.8s7.9-21.5,9.4-27.1s4.8-18.5,6.1-22.4s2.5-5.6,3.6-5.5
	s0.2,2.6-1.1,7s-5.4,18.5-7.1,25s-2.2,9.6,0,12s7.1-0.5,8.2-2.5s3-11,5.7-16.3c1.7-3.4,3.7-11.2,6.5-19.4c1.7-4.8,4.3-9.7,5.6-13.8
	c0.4-1.1,0.7-2.2,1-3.2c2.7-9.4,3.6-16.6,6-25.3C162.5,643.7,160,641.7,160.8,633.2z"
            />
            {this.toolTip(
              "FRONT_OF_LEFT_UPPER_LIMB",
              this.props.t("bodyMap.FRONT_OF_LEFT_UPPER_LIMB"),
              "left"
            )}

            <path
              onClick={() => this.changeView("frontRightUpperLimb")}
              id="FRONT_OF_RIGHT_UPPER_LIMB"
              d="M642.2,633.2c-0.1-1-0.3-2.2-0.7-3.5C624,579,582,550,563,490c-3.7-13.7-5.3-26.3-7-37l-29-58l-5.1-115.8
	c7.9-12.4,13.1-21.6,25-32.2c17.3,10.5,25.8,21.9,30.1,34c8,22,6.3,53.7,7,67c6,9,23,41,28,71c32,43,41,118,55,158l17,34
	c1.9,1.6,5.3,3,12.8,5.1c1.9,0.5,3.8,1.3,5.9,2.2c12.7,5.8,27.9,18.4,31.9,21c4.6,3,7.8,8.4,11.9,10.5s4.1,8.6,0,10.5
	s-9.6,0.1-13-1.5s-9-8.5-15.5-10.5c-3.8-1.2-7.6-1.5-7.6,0.1s8.5,22.4,9.1,25.5s3.9,13.9,5.9,21.1s5.5,21.2,6.8,29.2
	s-0.9,11.8-5.1,12.6s-7.9-7.6-8.6-10.6s-2.9-12.8-4-15.5s-6.6-18.8-8-22s-2.4-8.6-4.4-7.8s0.4,5.9,1.8,9.8s7.6,24,8.4,27.9
	s5,19.8,5.2,22.5s1.9,8.8-2.8,10.5s-7.8-1.9-9.5-5.6s-5.4-17.6-7.4-26.1s-5.8-19.5-7.1-24s-2.1-8-3.9-7.9s-1.5,2.5-0.8,4.8
	s6.1,20.6,7.1,25.6s4.2,16.1,5.4,20.4s1.8,9.2-2.5,10.8s-6.9-1.5-8.8-4.8s-7.9-21.5-9.4-27.1s-4.8-18.5-6.1-22.4s-2.5-5.6-3.6-5.5
	s-0.2,2.6,1.1,7s5.4,18.5,7.1,25s2.2,9.6,0,12s-7.1-0.5-8.2-2.5s-3-11-5.7-16.3c-1.7-3.4-3.7-11.2-6.5-19.4
	c-1.7-4.8-4.3-9.7-5.6-13.8c-0.4-1.1-0.7-2.2-1-3.2c-2.7-9.4-3.6-16.6-6-25.3C640.5,643.7,643,641.7,642.2,633.2z"
            />
            {this.toolTip(
              "FRONT_OF_RIGHT_UPPER_LIMB",
              this.props.t("bodyMap.FRONT_OF_RIGHT_UPPER_LIMB"),
              "right"
            )}
            <path
              onClick={() => this.changeView("frontLeftLowerLimb")}
              id="FRONT_OF_LEFT_LOWER_LIMB"
              d="M 292.327 590.5 c -2.021 14.389 -3.102 29.611 -2.827 34 c 0.5 8 -6.5 46 -11.5 70 c -3.981 19.107 -12.131 56.915 -14.375 92.478 c -0.575 9.105 0.172 18.063 0.375 26.522 c 0.845 35.062 9.541 55.489 16.139 69.427 C 300 921 290 978 286 1019 C 291 1080 299 1141 301 1202 C 297 1212 302 1228 299 1235 C 291 1258 274 1277 264 1288 C 256 1302 260 1311 259 1314 S 263 1317 263 1316 S 265 1321 269 1319 C 270 1325 273 1324 277 1324 C 280 1329 284 1329 288 1326 C 293 1336 311 1334 316 1319 C 332 1304 326 1301 333 1272 C 347 1268 354 1249 346 1227 C 350 1217 348 1204 343 1188 C 344 1155 356 1066 367 1059 C 369 1027 365 999 358 967 C 366 959 360 935 368.623 917.285 c 2.478 -11.204 8.03 -39.965 9.627 -52.285 c 1.75 -13.5 10.083 -66.333 11.815 -88.167 s 1.269 -38.833 0.435 -43.166 s -0.167 -12.667 -0.417 -21.334 s 3.083 -10.166 4.083 -12.333 c -3.834 -8.171 -10.12 -17.359 -17.755 -26.864 C 348.538 638.439 302.667 599.527 292.327 590.5 z"
            />
            {this.toolTip(
              "FRONT_OF_LEFT_LOWER_LIMB",
              this.props.t("bodyMap.FRONT_OF_LEFT_LOWER_LIMB"),
              "left"
            )}

            <path
              onClick={() => this.changeView("frontRightLowerLimb")}
              id="FRONT_OF_RIGHT_LOWER_LIMB"
              d="M 510.673 590.5 c 2.021 14.389 3.102 29.611 2.827 34 c -0.5 8 6.5 46 11.5 70 c 3.981 19.107 12.131 56.915 14.375 92.478 c 0.575 9.105 -0.172 18.063 -0.375 26.522 c -0.845 35.062 -9.541 55.489 -16.139 69.427 C 503 921 513 978 517 1019 C 512 1080 504 1141 502 1202 C 506 1212 501 1228 504 1235 C 512 1258 529 1277 539 1288 C 547 1302 543 1311 544 1314 S 540 1317 540 1316 S 538 1321 534 1319 C 533 1325 530 1324 526 1324 C 523 1329 519 1329 515 1326 C 510 1336 492 1334 487 1319 C 471 1304 477 1301 470 1272 C 456 1268 449 1249 457 1227 C 453 1217 455 1204 460 1188 C 459 1155 447 1066 436 1059 C 434 1027 438 999 445 967 C 437 959 443 935 434.377 917.285 c -2.478 -11.204 -8.03 -39.965 -9.627 -52.285 c -1.75 -13.5 -10.083 -66.333 -11.815 -88.167 s -1.269 -38.833 -0.435 -43.166 s 0.167 -12.667 0.417 -21.334 s -3.083 -10.166 -4.083 -12.333 c 3.834 -8.171 10.12 -17.359 17.755 -26.864 C 454.462 638.439 500.333 599.527 510.673 590.5 z"
            />
            {this.toolTip(
              "FRONT_OF_RIGHT_LOWER_LIMB",
              this.props.t("bodyMap.FRONT_OF_RIGHT_LOWER_LIMB"),
              "right"
            )}

            <g id="frt_spots"></g>
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBack() {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("front")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "10rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "28rem",
            left: "2rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "23.5rem",
            left: "16.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="800px"
            height="100%"
            viewBox="0 0 800 1360"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x="39"
              width={this.state.isFirefox ? null : null}
              height="1367"
              xlinkHref={require("../images/Body-Back v2.png")}
            ></image>
            <path
              onClick={() => this.changeView("bHead")}
              id="BODY_BACK_OF_HEAD"
              d="M450.06,200.29c-1.39-9.84-2.06-31.35-0.22-34.79c1.83-3.44,4.13-20.87,4.13-20.87s3.21,5.27,6.65,1.83
	s12.08-20.79,12.69-31.8c0.61-11.01-3.36-17.12-7.03-14.83s-4.28,5.81-4.89,5.35c-0.61-0.46,1.22-13.15,1.22-13.15
	s0.05-22.52-1.83-28.77S445.48,20.5,404.51,15c-39.9-0.91-60.45,39.51-62.17,48.27s2,40.73,2,40.73s-1.12,2.88-3.26-0.94
	s-6.42-4.43-8.25,0.31s1.22,26.9,7.64,36.54s7.57,7.6,9.75,4.34c0,0,1.72-0.87,1.87,4.99s5.5,21.4,4.89,24.07
	c-0.61,2.68,0.76,22.09-3.52,26.98c-2.22,2.53,24.05,10.85,49.38,10.54C426.42,210.53,450.74,205.03,450.06,200.29z"
            />
            {this.toolTip(
              "BODY_BACK_OF_HEAD",
              this.props.t("bodyMap.BACK_OF_HEAD"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012083",
                  this.props.t("bodyMap.BACK_OF_RIGHT_SHOULDER")
                )
              }
              id="BACK_OF_RIGHT_SHOULDER"
              d="M568.67,253.02c0,0-9.63-7.49-37.91-16.05c-28.28-8.56-67.43-21.08-79.65-36.68c0,0,0.3,2.97-11.73,5.65
	s-28.15,5.45-37.65,4.89c0,0,34.7,53.06,143.93,88.94C545.66,299.77,560.41,260.97,568.67,253.02z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_SHOULDER",
              this.props.t("bodyMap.BACK_OF_RIGHT_SHOULDER"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000018370",
                  this.props.t("bodyMap.BACK_OF_LEFT_SHOULDER")
                )
              }
              id="BACK_OF_LEFT_SHOULDER"
              d="M235.9,253.02c0,0,9.63-7.49,37.91-16.05c28.28-8.56,67.43-21.08,79.65-36.68c0,0-0.3,2.97,11.73,5.65
	s28.15,5.45,37.65,4.89c0,0-34.7,53.06-143.93,88.94C258.91,299.77,244.15,260.97,235.9,253.02z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_SHOULDER",
              this.props.t("bodyMap.BACK_OF_LEFT_SHOULDER"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012203",
                  this.props.t("bodyMap.UPPER_BACK")
                )
              }
              id="UPPER_BACK"
              d="M540,350.39c1-16.81,0-36.99,4.55-50.16c-69.19-26.71-122.19-55.7-141.71-89.41
	c-27.77,35.61-75.26,65.4-143.93,88.94c0,0,4.09,12.95,4.09,38.23c0,47.04,10,24,4.62,66.38c1.12,0,1.5,3.5,1.88,6.12
	s4.25,16.75,9.25,23S288,458.5,292,466c4.47,5.51,41.37,10.64,83.75,11.48c9.66,0.19,19.6-1.73,29.5-1.78
	c9.98-0.04,19.92,1.79,29.5,1.51c39.58-1.16,72.98-6.35,77.2-11.47c2.61-5.71,6.41-14.64,7.47-18.17c1.5-5,2.67-9.17,4.83-12.67
	s7.83-18.08,8.67-21.08s2.17-9.42,3.33-9.5C531.08,388.46,537,368.06,540,350.39z"
            />
            {this.toolTip(
              "UPPER_BACK",
              this.props.t("bodyMap.UPPER_BACK"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012147",
                  this.props.t("bodyMap.LOWER_BACK")
                )
              }
              id="LOWER_BACK"
              d="M434.499,475.97c-9.58,0.281-19.521-1.556-29.499-1.512c-9.906,0.043-19.847,1.969-29.504,1.776c-42.373-0.846-79.277-5.978-83.746-11.485c4,7.5,6.5,19,8.5,37.75s-2.25,32-3.25,37.75s-0.227,23.88,1.25,28c1.412,3.939,3.607,9.041-0.422,15.812c6.278-9.18,30.556-16.657,56.643-16.657c29.53,0,31.03,10.279,51.53,10.279c19,0,26-10.042,51.526-10.166c25.239-0.123,43.853,7.19,48.38,16.593c-0.532-1.279-0.915-2.17-1.072-2.61c-0.834-2.333-1.166-6.167-0.333-8.167s2.667-12.833,2.833-19s-3.667-30-4.667-34.833s1.667-28.5,2.334-33.333s3-14.667,4.333-16.833c0.392-0.637,1.273-2.456,2.361-4.833C507.479,469.625,474.078,474.807,434.499,475.97z"
            />
            {this.toolTip(
              "LOWER_BACK",
              this.props.t("bodyMap.LOWER_BACK"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012099",
                  this.props.t("bodyMap.BUTT_TEXT")
                )
              }
              id="BUTT_TEXT"
              d="M457.526,567.518C432,567.642,425,577.684,406,577.684c-20.5,0-22-10.279-51.53-10.279c-26.087,0-50.365,7.477-56.643,16.657c-0.185,0.311-0.366,0.62-0.578,0.938c-6,9-12,51.75-11.5,64s-2.5,24-4,32.5c0,19,7.324,25.063,15.316,37.142C317.76,749.914,355.235,772.904,389.5,739c2.75-2.875,6.75-8.875,7.75-11.625s2-3.25,4.375-3.25s3.75,1.125,4.25,2.875s3.792,8.5,7.292,11.334c37.774,39.903,74.878,12.96,94.414-18.404c8.533-13.701,14.134-14.93,14.134-38.43c-1.558-8.437-3.389-18.087-4.048-21.667c-1.167-6.333-0.5-24.333-2.666-42.667c-1.622-13.732-6.051-25.594-8.521-31.664c-0.206-0.505-0.397-0.97-0.573-1.393C501.379,574.708,482.766,567.395,457.526,567.518z"
            />
            {this.toolTip("BUTT_TEXT", this.props.t("bodyMap.BUTT_TEXT"), "left")}

            <path
              onClick={() => this.changeView("backRightUpperLimb")}
              id="BACK_OF_LEFT_UPPER_LIMB"
              d="M113.83,636.5c-3.17,1.12-14.17,7-17.83,8.5s-5.83,6.5-10.17,9s-8.33,6-9,8.83s-5.5,4.33-5.5,7.33
	s2,5.33,6.88,6s12.62-8,14.12-9.5s2.5,0.5,1.83,2.33s-5.67,15-6.83,19.83c-1.17,4.83-3.83,17-4.5,21s-3,21-3.33,24
	s-3.33,15,1.17,18.33s7.83-2.33,9.83-7.67s1.5-11.83,2.67-14.5s4.33-19,6.33-22.5s2.83,1.17,1.67,4.17s-3.83,16.17-3.83,18.33
	s-1.83,14-2.5,18s-1.33,14,0,18.17s7.17,1.67,9-0.5s3.67-11.17,4.5-16.5s1-14.17,2.53-20s3.64-16.33,5.47-19.17s3.83,0.33,3.33,2.5
	s-2.33,9.5-4,16.33s-1.5,14.5-3,21.33s-3.17,12.33,0,15.83s6.5,0.83,8.5-1.67s4.33-13.33,5.67-21.83s4.67-21.17,5.83-25.17
	c1.17-4,3.5-7.83,5.33-7.5c1.83,0.33-0.17,6-1,9.17s-5,20.67-5.17,26.33s2.5,7.83,5.67,6.5s4.33-6,5-8.83s2.67-8.67,3.17-12
	s4.17-16.17,6.17-20.33c2-4.17,2.83-9.33,6.67-27.33c3.84-18,1.49-22.33,0.49-26.33c5-18,20-53,49-88c11-19,26-53,32-103l27-60
	c8-38-4-17-5-93c0.89-11.06-24.41-61.71-26.1-59.98c0,0-16.05,14.06-20.9,24.98c-11,20-7,47-8,71c-25,44-28,74-32,92
	c-33,45-34,140-55,192L113.83,636.5z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_UPPER_LIMB",
              this.props.t("bodyMap.BACK_OF_LEFT_UPPER_LIMB"),
              "left"
            )}

            <path
              onClick={() => this.changeView("backLeftUpperLimb")}
              id="BACK_OF_RIGHT_UPPER_LIMB"
              d="M689.67,636.5c3.17,1.12,14.17,7,17.83,8.5s5.83,6.5,10.17,9s8.33,6,9,8.83s5.5,4.33,5.5,7.33s-2,5.33-6.88,6
	s-12.62-8-14.12-9.5s-2.5,0.5-1.83,2.33s5.67,15,6.83,19.83c1.17,4.83,3.83,17,4.5,21s3,21,3.33,24s3.33,15-1.17,18.33
	s-7.83-2.33-9.83-7.67s-1.5-11.83-2.67-14.5S706,711,704,707.5s-2.83,1.17-1.67,4.17s3.83,16.17,3.83,18.33s1.83,14,2.5,18
	s1.33,14,0,18.17s-7.17,1.67-9-0.5s-3.67-11.17-4.5-16.5s-1-14.17-2.53-20S689,712.83,687.17,710s-3.83,0.33-3.33,2.5
	s2.33,9.5,4,16.33s1.5,14.5,3,21.33s3.17,12.33,0,15.83s-6.5,0.83-8.5-1.67S678,751,676.67,742.5s-4.67-21.17-5.83-25.17
	c-1.17-4-3.5-7.83-5.33-7.5c-1.83,0.33,0.17,6,1,9.17s5,20.67,5.17,26.33s-2.5,7.83-5.67,6.5s-4.33-6-5-8.83s-2.67-8.67-3.17-12
	s-4.17-16.17-6.17-20.33c-2-4.17-2.83-9.33-6.67-27.33c-3.84-18-1.49-22.33-0.49-26.33c-5-18-20-53-49-88c-11-19-26-53-32-103
	l-27-60c-8-38,4-17,5-93c4.43-22.94,13.68-42.71,26.73-59.98c7.56,4.51,14.46,12.16,20.27,24.98c11,20,7,47,8,71c25,44,28,74,32,92
	c33,45,34,140,55,192L689.67,636.5z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_UPPER_LIMB",
              this.props.t("bodyMap.BACK_OF_RIGHT_UPPER_LIMB"),
              "right"
            )}

            <path
              onClick={() => this.changeView("backRightLowerLimb")}
              id="BACK_OF_LEFT_LOWER_LIMB"
              d="M 297.066 718.642 c -7.993 -12.078 -15.316 -18.142 -15.316 -37.142 c -1.5 8.5 -8.25 43 -9.75 54 s -3 14.5 -7.25 46.75 s -1.25 76 2.75 93.5 S 280.25 912 282.75 925 C 294 982 284 1025 282 1061 C 275 1084 297 1184 294 1277 C 292.3333 1279 290.6667 1281 289 1283 C 285 1278 281 1285 279 1287 C 275 1287 272 1291 271 1293 C 268 1294 260 1298 264 1304 C 264 1322 284 1318.6667 294 1326 C 303 1333 318 1345 337 1340 C 355 1325 343 1314.6667 346 1302 C 343 1290 346.6667 1276.6667 347 1264 C 345 1256 341 1248 342 1239 C 347 1213 352 1185 353 1154 C 380 1110 360 1053 359 1010 C 357 997 365 1005 367 960 C 374.3893 918.9997 377.1117 899.6663 379.834 880.333 s 9.5 -61.333 10.5 -78.667 s 1 -34.999 0.167 -40.999 s -1.625 -16.292 -1 -21.667 C 355.235 772.904 317.76 749.914 297.066 718.642 z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_LOWER_LIMB",
              this.props.t("bodyMap.BACK_OF_LEFT_LOWER_LIMB"),
              "left"
            )}

            <path
              onClick={() => this.changeView("backLeftLowerLimb")}
              id="BACK_OF_RIGHT_LOWER_LIMB"
              d="M 506.934 718.642 c 7.993 -12.078 15.316 -18.142 15.316 -37.142 c 1.5 8.5 8.25 43 9.75 54 s 3 14.5 7.25 46.75 s 1.25 76 -2.75 93.5 S 523.75 912 521.25 925 C 510 982 520 1025 522 1061 C 529 1084 507 1184 510 1277 C 511.6667 1279 513.3333 1281 515 1283 C 519 1278 523 1285 525 1287 C 529 1287 532 1291 533 1293 C 536 1294 544 1298 540 1304 C 540 1322 520 1318.6667 510 1326 C 501 1333 486 1345 467 1340 C 449 1325 461 1314.6667 458 1302 C 461 1290 457.3333 1276.6667 457 1264 C 459 1256 463 1248 462 1239 C 457 1213 452 1185 451 1154 C 424 1110 444 1053 445 1010 C 447 997 439 1005 437 960 C 429.6107 918.9997 426.8883 899.6663 424.166 880.333 s -9.5 -61.333 -10.5 -78.667 s -1 -34.999 -0.167 -40.999 s 1.625 -16.292 1 -21.667 C 448.765 772.904 486.24 749.914 506.934 718.642 z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_LOWER_LIMB",
              this.props.t("bodyMap.BACK_OF_RIGHT_LOWER_LIMB"),
              "right"
            )}

            <g id="bck_spots"></g>
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBhead() {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("head")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "10rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "19rem",
            right: "5rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "14.5rem",
            left: "22.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
        <svg
          ref={this.svgRef}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          width="740px"
          height="100%"
          viewBox="0 0 740 886"
          xmlSpace="preserve"
        >
          <image
            overflow="visible"
            x={this.state.isFirefox ? "4" : null}
            y={this.state.isFirefox ? "88" : null}
            width={this.state.isFirefox ? "99%" : "740"}
            height={this.state.isFirefox ? "64%" : "740"}
            xlinkHref={require("../images/Back-Of-Head v2.png")}
          ></image>
          <path
            onClick={() =>
              this.onBodyClick(
                "20000012051",
                this.props.t("bodyMap.BACK_OF_HEAD")
              )
            }
            id="HEAD_BACK_OF_HEAD"
            d="M232.23,342.75c0,0-4.28-63.44-1.83-79.03c2.45-15.59,10.82-59.55,55.55-93.98
	c44.73-34.43,89.52-36.7,118.34-26.24c28.82,10.46,61.87,29.45,84.63,61.84c22.76,32.39,31.62,69.99,20.31,140.62
	c0,0-3.67,50.75-16.2,85.3c0,0-7.34,39.01-5.96,51.45s-58.32,37.43-118.66,35.94s-97.03-13.84-111.1-30.9
	c0,0-6.42-60.78-12.23-78.21C239.26,392.13,232.23,342.75,232.23,342.75z"
          />
          {this.toolTip(
            "HEAD_BACK_OF_HEAD",
            this.props.t("bodyMap.BACK_OF_HEAD"),
            "top"
          )}

          <path
            onClick={() =>
              this.onBodyClick(
                "20000018368",
                this.props.t("bodyMap.BACK_OF_LEFT_EAR_FLAP")
              )
            }
            id="BACK_OF_LEFT_EAR_FLAP"
            d="M232.14,341.4c0,0-6.94-10.42-16.42-4.61s-13.76,12.84-10.09,36.08s14.06,45.86,14.06,45.86
	s8.87,20.18,18.96,18.96s10.97-7.44,10.97-7.44l-8.51-36.11C241.12,394.13,232.43,345.97,232.14,341.4z"
          />
          {this.toolTip(
            "BACK_OF_LEFT_EAR_FLAP",
            this.props.t("bodyMap.BACK_OF_LEFT_EAR_FLAP"),
            "left"
          )}

          <path
            onClick={() =>
              this.onBodyClick(
                "20000018369",
                this.props.t("bodyMap.BACK_OF_RIGHT_EAR_FLAP")
              )
            }
            id="BACK_OF_RIGHT_EAR_FLAP"
            d="M510.02,344.18c0,0,8.32-14.5,15.84-8c7.52,6.5,14.21,11.04,12.73,25.68s-7.05,40.51-10.78,48.36
	s-12.78,36.94-34.79,21.04c0,0,9.81-29.51,13.63-61.51C510.47,337.75,510.02,344.18,510.02,344.18z"
          />
          {this.toolTip(
            "BACK_OF_RIGHT_EAR_FLAP",
            this.props.t("bodyMap.BACK_OF_RIGHT_EAR_FLAP"),
            "right"
          )}

          <path
            onClick={() =>
              this.onBodyClick(
                "20000012067",
                this.props.t("bodyMap.BACK_OF_NECK")
              )
            }
            id="BACK_OF_NECK"
            d="M135.31,653.33c0,0,114.65-40.15,123.52-75.01c8.87-34.85-1.53-90.56-1.53-90.56s18.65,25.8,88.35,29.75
	c69.71,3.95,107.06-9.85,127.29-20.99c11.47-8.16,11.47-8.16,11.47-8.16s-8.21,84.85,1.88,96.08s29.35,32.56,91.95,56.87
	c35.08,12.03,30.5,12.03,30.5,12.03H135.31z"
          />
          {this.toolTip("BACK_OF_NECK", this.props.t("bodyMap.BACK_OF_NECK"), "bottom")}
        </svg>
        { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyHead() {
    return (
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("bHead")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "10rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "18rem",
            right: "2rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "13.5rem",
            left: "19.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 886"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="886"
              xlinkHref={require("../images/Front-Of-Head v2.png")}
            ></image>
            <path
              onClick={() => this.changeView("leftEar")}
              id="LEFT_EAR"
              class="head_frt_1"
              d="M 150 378 C 162 426 165 459 184 525 C 110 538 65 365 153 378 Z"
            />
            {this.toolTip("LEFT_EAR", this.props.t("bodyMap.LEFT_EAR"), "left")}
            <path
              onClick={() => this.changeView("rightEar")}
              id="RIGHT_EAR"
              class="head_frt_2"
              d="M 583 387 L 555 528 C 642 533 662 361 591 386 Z"
            />
            {this.toolTip("RIGHT_EAR", this.props.t("bodyMap.RIGHT_EAR"), "right")}
            <path
              onClick={() => this.changeView("leftEye")}
              id="LEFT_EYE"
              class="head_frt_3"
              d="M 200 347 C 189 401 198 438 219 452 C 255 456 367 506 314 355 C 296 343 240 333 204 344 Z"
            />
            {this.toolTip("LEFT_EYE", this.props.t("bodyMap.LEFT_EYE"), "left")}
            <path
              onClick={() => this.changeView("rightEye")}
              id="RIGHT_EYE"
              class="head_frt_4"
              d="M 426 356 C 435 414 415 431 429 456 C 536 485 567 417 545 347 C 517 335 447 339 430 353 Z"
            />
            {this.toolTip("RIGHT_EYE", this.props.t("bodyMap.RIGHT_EYE"), "right")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012154",
                  this.props.t("bodyMap.NOSE_TEXT")
                )
              }
              id="NOSE_TEXT"
              class="head_frt_5"
              d="M 345 366 C 342 391 339 484 320 505 C 334 543 405 539 415 505 C 396 482 396 360 393 366 Z"
            />
            {this.toolTip("NOSE_TEXT", this.props.t("bodyMap.NOSE_TEXT"), "bottom")}
            <path
              onClick={() => this.changeView("mouth")}
              id="MOUTH_TEXT"
              class="head_frt_6"
              d="M 300 569 C 339 574 397 577 440 568 C 399 639 334 634 300 572 Z"
            />
            {this.toolTip("MOUTH_TEXT", this.props.t("bodyMap.MOUTH_TEXT"), "bottom")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012129",
                  this.props.t("bodyMap.JAW_TEXT")
                )
              }
              id="JAW_TEXT"
              class="head_frt_7"
              d="M 196 560 C 371 670 424 637 541 572 C 485 739 285 784 192 565 Z"
            />
            {this.toolTip("JAW_TEXT", this.props.t("bodyMap.JAW_TEXT"), "bottom")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012103",
                  this.props.t("bodyMap.FOREHEAD_TEXT")
                )
              }
              id="FOREHEAD_TEXT"
              class="head_frt_8"
              d="M 231 254 C 213 276 153 260 170 331 H 574 C 584 258 484 201 462 183 C 506 198 345 222 276 201 Z"
            />
            {this.toolTip(
              "FOREHEAD_TEXT",
              this.props.t("bodyMap.FOREHEAD_TEXT"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012101",
                  this.props.t("bodyMap.CROWN_TEXT")
                )
              }
              id="CROWN_TEXT"
              class="head_frt_9"
              d="M 135 282 C 156 12 555 0 591 270 C 420 147 252 150 138 285 Z"
            />
            {this.toolTip(
              "CROWN_TEXT",
              this.props.t("bodyMap.CROWN_TEXT"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012133",
                  this.props.t("bodyMap.LEFT_CHEEK")
                )
              }
              id="LEFT_CHEEK"
              class="head_frt_12"
              d="M 201 471 C 192 513 201 543 204 561 C 234 570 258 555 306 561 C 312 528 312 507 306 468 Z"
            />
            {this.toolTip(
              "LEFT_CHEEK",
              this.props.t("bodyMap.LEFT_CHEEK"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012172",
                  this.props.t("bodyMap.RIGHT_CHEEK")
                )
              }
              id="RIGHT_CHEEK"
              class="head_frt_11"
              d="M 546 471 C 549 495 552 522 528 561 C 492 561 450 555 435 549 C 426 519 444 504 432 471 Z"
            />
            {this.toolTip(
              "RIGHT_CHEEK",
              this.props.t("bodyMap.RIGHT_CHEEK"),
              "right"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012238",
                  this.props.t("bodyMap.FRONT_OF_NECK")
                )
              }
              id="FRONT_OF_NECK"
              class="head_frt_13"
              d="M 594 774 C 504 741 504 714 510 618 C 411 741 300 735 231 627 C 231 696 246 702 165 774 Z"
            />
            {this.toolTip(
              "FRONT_OF_NECK",
              this.props.t("bodyMap.FRONT_OF_NECK"),
              "bottom"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyMouth() {
    return (
      <div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "19rem",
            right: "5rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "14.5rem",
            left: "22.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="400px"
            viewBox="0 0 740 583"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="583"
              xlinkHref={require("../images/Mouth v1.png")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012205",
                  this.props.t("bodyMap.UPPER_LIP")
                )
              }
              id="UPPER_LIP"
              d="M 41 338 C 91.22 340.01 98.05 341.65 104 340.79 C 108.28 340.18 126.92 331.77 132 329.47 C 170 312.45 236 265.15 236 265.15 C 236 265.15 255.58 251.39 255.58 251.39 C 258.14 249.55 261.96 246.08 265 245.45 C 354.72 275.56 370.64 282.07 376 281.15 C 464.05 246.58 467.67 244.71 471 245.29 C 474.44 245.89 482.58 250.84 486 252.69 C 609 316.25 643 333.94 643 333.94 C 646.86 335.97 655.08 340.85 659 341.6 C 659 341.6 688 339 688 339 C 510.57 166.57 500.46 155.86 497 153.98 C 493.68 152.18 484.12 151.34 480 150.85 C 480 150.85 454 147.34 454 147.34 C 447.81 147.05 435.54 157.04 430 160.66 C 430 160.66 403 177.79 403 177.79 C 403 177.79 387 182.13 387 182.13 C 383.31 183.05 376.54 185.04 373 184.86 C 367.43 184.57 355.54 177.85 350 175.31 C 294.33 149.55 281.4 142.6 277 142.5 C 277 142.5 252 147.58 252 147.58 C 248.4 148.37 243.05 149.02 240 150.85 C 51.73 326.75 46.15 330.8 41 338 Z"
            />
            {this.toolTip("UPPER_LIP", this.props.t("bodyMap.UPPER_LIP"), "top")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012149",
                  this.props.t("bodyMap.LOWER_LIP")
                )
              }
              id="LOWER_LIP"
              d="M 45 343 C 45 343 100 411 100 411 C 156 486 186 501.05 186 501.05 C 186 501.05 209 516.01 209 516.01 C 209 516.01 230 523.69 230 523.69 C 239.4 527.04 241.61 528.98 252 529 C 406 535 423 531 423 531 C 476.45 530.98 483.9 525.15 494 520.26 C 531.41 500.92 539.56 492.44 543 489 C 676.29 356.71 683.79 349.87 688 344 C 688 344 662 339.18 662 339.18 C 662 339.18 631 342.85 631 342.85 C 631 342.85 584 349.3 584 349.3 C 577.82 351.06 564.25 358.92 558 362.22 C 533 374.36 515 386 515 386 C 515 386 461 412.6 461 412.6 C 452.41 416.04 441.09 421.89 432 422 C 432 422 423 422 423 422 C 301 425 285 425.81 285 425.81 C 282.07 426.01 275.57 426.17 273 425.81 C 273 425.81 244 412.31 244 412.31 C 209 395.57 163 367.97 163 367.97 C 163 367.97 148 362 148 362 C 122.36 351.78 126.84 352.97 100 347.6 C 71 343 45 343 45 343 Z"
            />
            {this.toolTip("LOWER_LIP", this.props.t("bodyMap.LOWER_LIP"), "bottom")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012167",
                  this.props.t("bodyMap.PHILTRUM")
                )
              }
              id="PHILTRUM"
              d="M 1 339 C 1 339 31 336.17 31 336.17 C 48.46 332.18 56.4 323.42 60 320.17 C 227.84 162.33 234.73 154.43 239 152.43 C 268.05 146.56 273.13 145.33 276 145.78 C 276 145.78 298 155.14 298 155.14 C 298 155.14 344 175.86 344 175.86 C 369.93 185.92 375.88 184.42 379 183.8 C 379 183.8 401 179.4 401 179.4 C 415 175.44 434 157.27 434 157.27 C 438.06 154 443.22 152.67 448 150.8 C 448 150.8 464 144.97 464 144.97 C 467.59 144.41 471.55 146.24 475 147.28 C 475 147.28 496 153.67 496 153.67 C 503.98 156.46 506.66 160.81 512.42 166.58 C 681.66 331.66 690.1 340.75 693 341.77 C 727.28 327.84 730.57 326.79 731.98 324.61 C 733.13 322.83 733.22 315.71 733.79 313 C 733.95 252.06 729.12 248.59 726 245.85 C 517.05 64.88 514.1 65 512 65 C 220.34 63.02 219.38 66.62 212 74 C 27 261 19 250 2 277 Z"
            />
            {this.toolTip(
              "PHILTRUM",
              this.props.t("bodyMap.PHILTRUM"),
              "top"
            )}
            <path
              onClick={() => this.changeView("mouthCavity")}
              id="MOUTH_CAVITY"
              d="M 373.039 285.532 L 468.682 247.959 L 645.733 339.046 C 526.181 345.309 505.686 408.501 441.355 417.04 L 272.843 420.456 C 219.898 403.946 164.107 361.818 108.885 344.739 C 161.83 320.259 212.4977 282.4957 264.304 251.374 L 373.039 285.532 Z"
            />
            {this.toolTip("MOUTH_CAVITY", this.props.t("bodyMap.MOUTH_CAVITY"), "top")}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyMouthCavity() {
    return (
      <div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "19rem",
            right: "5rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "14.5rem",
            left: "22.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 855"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "95" : null}
              // width="740"
              width={this.state.isFirefox ? "74%" : "740"}
              height="820"
              xlinkHref={require("../images/Mouth-Cavity v1.png")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012204",
                  this.props.t("bodyMap.RIGHT_UPPER_GUM")
                )
              }
              id="RIGHT_UPPER_GUM"
              d="M 381.192 79.482 L 383.731 149.294 C 393.885 132.793 543.662 137.87 586.818 260.992 C 560.163 158.179 524.623 31.249 381.192 78.213 Z"
            />
            {this.toolTip(
              "RIGHT_UPPER_GUM",
              this.props.t("bodyMap.RIGHT_UPPER_GUM"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012228",
                  this.props.t("bodyMap.LEFT_UPPER_GUM")
                )
              }
              id="LEFT_UPPER_GUM"
              d=" M 380.973 78.36 L 383.89 149.11 C 306.575 130.146 191.332 178.285 182.58 268.729 C 206.65 138.899 229.99 46.267 381.702 78.36 Z"
            />
            {this.toolTip(
              "LEFT_UPPER_GUM",
              this.props.t("bodyMap.LEFT_UPPER_GUM"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012206",
                  this.props.t("bodyMap.RIGHT_UPPER_TEETH")
                )
              }
              id="RIGHT_UPPER_TEETH"
              d=" M 383.89 149.839 C 410.148 126.499 599.789 151.298 599.789 339.48 L 550.92 341.668 C 532.685 305.928 492.569 269.459 496.216 229.342 C 497.675 214.755 419.63 210.378 392.643 205.273 L 383.89 149.839 Z"
            />
            {this.toolTip(
              "RIGHT_UPPER_TEETH",
              this.props.t("bodyMap.RIGHT_UPPER_TEETH"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012229",
                  this.props.t("bodyMap.LEFT_UPPER_TEETH")
                )
              }
              id="LEFT_UPPER_TEETH"
              d="M 383.521 150.456 L 392.454 205.547 C 353.7413 211.5027 281.528 207.036 276.316 223.414 C 280.039 270.316 240.582 305.307 220.481 337.319 L 173.579 335.83 C 160.923 155.667 339.597 131.844 383.521 150.456 Z"
            />
            {this.toolTip(
              "LEFT_UPPER_TEETH",
              this.props.t("bodyMap.LEFT_UPPER_TEETH"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012127",
                  this.props.t("bodyMap.ROOF_OF_MOUTH")
                )
              }
              id="ROOF_OF_MOUTH"
              d="M 220.719 337.244 C 240.471 304.061 279.185 270.877 276.815 223.472 C 281.555 206.88 353.717 211.884 392.168 206.09 C 419.821 210.04 497.249 214.781 496.459 229.792 C 492.509 270.087 532.803 304.5873 550.975 341.985 C 437.993 311.962 320.27 310.381 220.719 337.244 Z"
            />
            {this.toolTip(
              "ROOF_OF_MOUTH",
              this.props.t("bodyMap.ROOF_OF_MOUTH"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012188",
                  this.props.t("bodyMap.RIGHT_OF_TONGUE")
                )
              }
              id="RIGHT_OF_TONGUE"
              d="M 563.187 531.68 C 567.451 460.272 512.03 470.93 502.437 466.667 C 464.069 436.825 456.609 376.075 379.872 381.404 L 375.609 565.786 C 396.924 542.338 456.609 556.193 563.187 530.615 Z"
            />
            {this.toolTip(
              "RIGHT_OF_TONGUE",
              this.props.t("bodyMap.RIGHT_OF_TONGUE"),
              "right"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012217",
                  this.props.t("bodyMap.LEFT_OF_TONGUE")
                )
              }
              id="LEFT_OF_TONGUE"
              d="M 380.01 381.795 L 375.422 566.221 C 351.566 541.447 287.338 557.963 213.934 543.282 C 190.996 486.395 222.192 473.549 268.069 463.456 C 304.771 435.012 308.441 374.454 380.01 381.795 Z"
            />
            {this.toolTip(
              "LEFT_OF_TONGUE",
              this.props.t("bodyMap.LEFT_OF_TONGUE"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012102",
                  this.props.t("bodyMap.FLOOR_OF_MOUTH")
                )
              }
              id="FLOOR_OF_MOUTH"
              d="M 533.24 535.942 C 458.001 553.375 388.268 546.035 376.34 566.221 C 351.566 542.365 277.245 554.293 228.615 546.953 C 242.378 595.582 442.403 691.007 533.24 535.942 Z"
            />
            {this.toolTip(
              "FLOOR_OF_MOUTH",
              this.props.t("bodyMap.FLOOR_OF_MOUTH"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012150",
                  this.props.t("bodyMap.RIGHT_LOWER_TEETH")
                )
              }
              id="RIGHT_LOWER_TEETH"
              d="M 533.24 535.942 C 551.591 534.107 573.612 527.684 581.869 562.551 C 546.085 690.089 450.661 707.523 383.68 687.337 L 383.68 621.274 C 404.783 618.521 482.775 623.109 533.24 535.942 Z"
            />
            {this.toolTip(
              "RIGHT_LOWER_TEETH",
              this.props.t("bodyMap.RIGHT_LOWER_TEETH"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012230",
                  this.props.t("bodyMap.LEFT_LOWER_TEETH")
                )
              }
              id="LEFT_LOWER_TEETH"
              d=" M 383.68 621.274 L 383.68 687.337 C 289.173 701.1 246.048 694.677 183.655 581.819 C 182.738 568.056 187.326 541.447 213.017 543.282 C 250.636 577.232 306.606 622.191 383.68 621.274 Z"
            />
            {this.toolTip(
              "LEFT_LOWER_TEETH",
              this.props.t("bodyMap.LEFT_LOWER_TEETH"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012148",
                  this.props.t("bodyMap.RIGHT_LOWER_GUM")
                )
              }
              id="RIGHT_LOWER_GUM"
              d="M 581.869 562.551 C 546.085 690.089 450.661 707.523 383.68 688.254 L 383.68 742.389 C 498.373 746.06 565.354 639.624 581.869 562.551 Z"
            />
            {this.toolTip(
              "RIGHT_LOWER_GUM",
              this.props.t("bodyMap.RIGHT_LOWER_GUM"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012186",
                  this.props.t("bodyMap.LEFT_LOWER_GUM")
                )
              }
              id="LEFT_LOWER_GUM"
              d=" M 383.68 687.337 L 383.68 741.472 C 337.803 753.4 227.698 717.616 183.655 582.737 C 246.048 693.76 291.008 702.017 383.68 688.254 Z"
            />

            {this.toolTip(
              "LEFT_LOWER_GUM",
              this.props.t("bodyMap.LEFT_LOWER_GUM"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000018372",
                  this.props.t("bodyMap.RIGHT_TONSIL")
                )
              }
              id="RIGHT_TONSIL"
              d="M518.93,468.86c0,0,28.31,3.81,35.02,18.36c0,0,6.02-41.29-7.74-72.17c-13.76-30.88-27.21-44.64-31.8-36.08 c-4.59,8.56-8.87,22.32-5.2,34.24S518.93,468.86,518.93,468.86z"
            />
            {this.toolTip(
              "RIGHT_TONSIL",
              this.props.t("bodyMap.RIGHT_TONSIL"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012189",
                  this.props.t("bodyMap.LEFT_TONSIL")
                )
              }
              id="LEFT_TONSIL"
              d="M248.86,467.01c0,0-28.01,5.57-33.81,20.51c0,0-8.58-40.84,3.23-72.51c11.81-31.68,24.38-46.24,29.49-37.99 c5.11,8.26,10.24,21.72,7.32,33.85C252.17,423,248.86,467.01,248.86,467.01z"
            />
            {this.toolTip(
              "LEFT_TONSIL",
              this.props.t("bodyMap.LEFT_TONSIL"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyRightEye() {
    return (
      <div style={{ paddingTop: 130 }}>
        <svg
          ref={this.svgRef}
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          width="740px"
          height="512"
          viewBox="0 0 740 512"
          xmlSpace="preserve"
        >
          <image
            overflow="visible"
            width="740"
            height="512"
            xlinkHref={require("../images/Eye(R) v1.jpg")}
          ></image>
          <path
            onClick={() =>
              this.onBodyClick(
                "20000012178",
                this.props.t("bodyMap.RIGHT_EYEBROW")
              )
            }
            id="RIGHT_EYEBROW"
            class="left_eye_frt_1"
            fill="none"
            stroke="black"
            stroke-width="1"
            d="M 653.00,254.00 C 650.25,247.56 641.25,238.81 636.27,233.00 622.60,217.06 598.50,194.95 582.00,181.54 560.15,163.79 536.68,148.89 511.00,137.31 476.36,121.69 433.81,112.51 396.00,109.91 396.00,109.91 374.00,108.00 374.00,108.00 374.00,108.00 334.00,108.00 334.00,108.00 334.00,108.00 322.00,108.91 322.00,108.91 322.00,108.91 298.00,110.83 298.00,110.83 263.36,114.15 229.68,120.35 196.00,129.27 163.64,137.83 125.33,150.70 96.00,166.70 84.61,172.91 67.74,182.35 59.30,192.01 57.82,193.71 56.07,196.05 54.95,198.00 50.24,206.19 52.63,211.13 61.00,214.83 68.63,218.19 81.64,219.99 90.00,220.00 90.00,220.00 99.00,220.00 99.00,220.00 108.04,219.89 122.15,216.23 131.00,213.71 154.76,206.95 177.21,196.39 200.00,187.01 247.60,167.43 288.31,152.84 340.00,147.83 340.00,147.83 362.00,146.00 362.00,146.00 362.00,146.00 381.00,146.00 381.00,146.00 395.32,146.02 409.87,148.52 424.00,150.57 478.45,158.47 530.01,179.90 578.00,206.30 578.00,206.30 632.00,239.86 632.00,239.86 632.00,239.86 653.00,254.00 653.00,254.00 Z"
          />
          {this.toolTip(
            "RIGHT_EYEBROW",
            this.props.t("bodyMap.RIGHT_EYEBROW"),
            "top"
          )}

          <path
            onClick={() =>
              this.onBodyClick(
                "20000012248",
                this.props.t("bodyMap.RIGHT_EYE_AND_LIDS")
              )
            }
            id="RIGHT_EYE_AND_LIDS"
            class="left_eye_frt_2"
            d="M 217.00,278.00 C 215.89,275.31 212.84,277.87 211.00,278.97 206.70,281.53 200.84,285.20 197.00,288.31 197.00,288.31 162.00,318.58 162.00,318.58 155.98,323.74 146.74,330.19 143.00,337.00 143.00,337.00 146.00,336.00 146.00,336.00 140.83,341.17 130.77,349.15 128.00,355.00 128.00,355.00 132.00,356.00 132.00,356.00 132.00,356.00 128.00,358.00 128.00,358.00 132.88,360.09 149.70,361.40 156.00,362.15 156.00,362.15 214.00,369.43 214.00,369.43 214.00,369.43 255.00,377.39 255.00,377.39 255.00,377.39 320.00,387.15 320.00,387.15 320.00,387.15 342.00,389.82 342.00,389.82 342.00,389.82 372.00,385.41 372.00,385.41 372.00,385.41 405.00,379.52 405.00,379.52 405.00,379.52 423.00,373.67 423.00,373.67 423.00,373.67 451.00,364.67 451.00,364.67 451.00,364.67 512.00,348.00 512.00,348.00 517.72,346.37 530.83,344.71 534.00,340.00 482.07,322.67 455.09,291.35 412.00,269.81 402.38,265.00 382.42,257.31 372.00,255.46 372.00,255.46 361.00,254.25 361.00,254.25 361.00,254.25 338.00,251.34 338.00,251.34 338.00,251.34 329.00,252.00 329.00,252.00 329.00,252.00 307.00,252.00 307.00,252.00 298.02,252.01 295.38,253.37 287.00,254.57 287.00,254.57 278.00,255.46 278.00,255.46 259.97,258.66 233.27,269.44 217.00,278.00 Z M 222.00,273.00 C 222.00,273.00 222.00,274.00 222.00,274.00 222.00,274.00 223.00,273.00 223.00,273.00 223.00,273.00 222.00,273.00 222.00,273.00 Z M 150.00,333.00 C 150.00,333.00 150.00,334.00 150.00,334.00 150.00,334.00 149.00,333.00 149.00,333.00 149.00,333.00 150.00,333.00 150.00,333.00 Z M 147.00,335.00 C 147.00,335.00 147.00,336.00 147.00,336.00 147.00,336.00 146.00,335.00 146.00,335.00 146.00,335.00 147.00,335.00 147.00,335.00 Z"
          />
          {this.toolTip(
            "RIGHT_EYE_AND_LIDS",
            this.props.t("bodyMap.RIGHT_EYE_AND_LIDS"),
            "bottom"
          )}

          <path
            onClick={() =>
              this.onBodyClick(
                "20000012184",
                this.props.t("bodyMap.RIGHT_UNDER_EYEBROW")
              )
            }
            id="UNDER_EYEBROW"
            class="left_eye_frt_2"
            d="M 102.00,223.00 C 102.00,223.00 105.00,225.00 105.00,225.00 96.30,229.29 92.47,236.08 89.34,245.00 87.29,250.85 84.58,258.78 85.18,265.00 86.44,278.26 100.38,293.55 112.00,298.96 115.85,300.75 121.75,301.89 126.00,301.99 135.49,302.20 146.71,299.26 155.00,294.65 155.00,294.65 165.00,288.20 165.00,288.20 165.00,288.20 181.00,279.38 181.00,279.38 181.00,279.38 193.00,271.05 193.00,271.05 193.00,271.05 210.00,261.57 210.00,261.57 221.15,255.19 231.89,248.08 244.00,243.47 244.00,243.47 262.00,239.44 262.00,239.44 262.00,239.44 285.00,227.77 285.00,227.77 305.36,221.50 326.96,221.97 348.00,222.00 348.00,222.00 363.00,223.00 363.00,223.00 363.00,223.00 391.00,225.00 391.00,225.00 405.34,225.07 406.86,226.66 420.00,229.87 420.00,229.87 433.00,233.03 433.00,233.03 444.88,236.80 442.12,238.03 457.00,240.00 457.00,240.00 456.00,243.00 456.00,243.00 456.00,243.00 461.00,244.00 461.00,244.00 463.81,240.59 468.20,242.22 472.00,243.30 472.00,243.30 501.00,250.29 501.00,250.29 501.00,250.29 522.00,256.03 522.00,256.03 522.00,256.03 536.00,261.26 536.00,261.26 550.45,265.94 557.99,267.39 572.00,274.78 572.00,274.78 596.00,289.42 596.00,289.42 601.85,293.40 612.22,300.37 619.00,301.69 629.87,303.80 634.20,300.41 643.00,294.96 646.50,292.80 652.29,290.00 654.95,287.22 661.65,280.19 660.74,270.68 656.00,263.00 656.00,263.00 660.00,265.00 660.00,265.00 655.95,260.35 643.62,252.29 638.00,248.29 620.11,235.55 601.07,224.04 582.00,213.15 546.98,193.16 509.82,176.08 471.00,164.91 471.00,164.91 444.00,158.50 444.00,158.50 444.00,158.50 424.00,154.29 424.00,154.29 407.45,151.74 390.71,151.00 374.00,151.00 374.00,151.00 352.00,151.00 352.00,151.00 352.00,151.00 334.00,152.72 334.00,152.72 334.00,152.72 319.00,154.27 319.00,154.27 319.00,154.27 280.00,162.73 280.00,162.73 241.57,172.87 205.70,189.49 169.00,204.31 147.71,212.90 125.33,223.00 102.00,223.00 Z M 454.00,243.00 C 454.00,243.00 454.00,244.00 454.00,244.00 454.00,244.00 455.00,243.00 455.00,243.00 455.00,243.00 454.00,243.00 454.00,243.00 Z"
          />
          {this.toolTip(
            "UNDER_EYEBROW",
            this.props.t("bodyMap.RIGHT_UNDER_EYEBROW"),
            "left"
          )}
        </svg>
        { this.renderDebugOverlay() }
      </div>
    );
  }
  bodyLeftEye() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="512"
            viewBox="0 0 740 512"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="512"
              xlinkHref={require("../images/Eye(R) v1.jpg")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012139",
                  this.props.t("bodyMap.LEFT_EYEBROW")
                )
              }
              id="LEFT_EYEBROW"
              class="left_eye_frt_1"
              fill="none"
              stroke="black"
              stroke-width="1"
              d="M 653.00,254.00 C 650.25,247.56 641.25,238.81 636.27,233.00 622.60,217.06 598.50,194.95 582.00,181.54 560.15,163.79 536.68,148.89 511.00,137.31 476.36,121.69 433.81,112.51 396.00,109.91 396.00,109.91 374.00,108.00 374.00,108.00 374.00,108.00 334.00,108.00 334.00,108.00 334.00,108.00 322.00,108.91 322.00,108.91 322.00,108.91 298.00,110.83 298.00,110.83 263.36,114.15 229.68,120.35 196.00,129.27 163.64,137.83 125.33,150.70 96.00,166.70 84.61,172.91 67.74,182.35 59.30,192.01 57.82,193.71 56.07,196.05 54.95,198.00 50.24,206.19 52.63,211.13 61.00,214.83 68.63,218.19 81.64,219.99 90.00,220.00 90.00,220.00 99.00,220.00 99.00,220.00 108.04,219.89 122.15,216.23 131.00,213.71 154.76,206.95 177.21,196.39 200.00,187.01 247.60,167.43 288.31,152.84 340.00,147.83 340.00,147.83 362.00,146.00 362.00,146.00 362.00,146.00 381.00,146.00 381.00,146.00 395.32,146.02 409.87,148.52 424.00,150.57 478.45,158.47 530.01,179.90 578.00,206.30 578.00,206.30 632.00,239.86 632.00,239.86 632.00,239.86 653.00,254.00 653.00,254.00 Z"
            />
            {this.toolTip("LEFT_EYEBROW", this.props.t("bodyMap.LEFT_EYEBROW"), "top")}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012247",
                  this.props.t("bodyMap.LEFT_EYE_AND_LIDS")
                )
              }
              id="LEFT_EYE_AND_LIDS"
              class="left_eye_frt_2"
              d="M 217.00,278.00 C 215.89,275.31 212.84,277.87 211.00,278.97 206.70,281.53 200.84,285.20 197.00,288.31 197.00,288.31 162.00,318.58 162.00,318.58 155.98,323.74 146.74,330.19 143.00,337.00 143.00,337.00 146.00,336.00 146.00,336.00 140.83,341.17 130.77,349.15 128.00,355.00 128.00,355.00 132.00,356.00 132.00,356.00 132.00,356.00 128.00,358.00 128.00,358.00 132.88,360.09 149.70,361.40 156.00,362.15 156.00,362.15 214.00,369.43 214.00,369.43 214.00,369.43 255.00,377.39 255.00,377.39 255.00,377.39 320.00,387.15 320.00,387.15 320.00,387.15 342.00,389.82 342.00,389.82 342.00,389.82 372.00,385.41 372.00,385.41 372.00,385.41 405.00,379.52 405.00,379.52 405.00,379.52 423.00,373.67 423.00,373.67 423.00,373.67 451.00,364.67 451.00,364.67 451.00,364.67 512.00,348.00 512.00,348.00 517.72,346.37 530.83,344.71 534.00,340.00 482.07,322.67 455.09,291.35 412.00,269.81 402.38,265.00 382.42,257.31 372.00,255.46 372.00,255.46 361.00,254.25 361.00,254.25 361.00,254.25 338.00,251.34 338.00,251.34 338.00,251.34 329.00,252.00 329.00,252.00 329.00,252.00 307.00,252.00 307.00,252.00 298.02,252.01 295.38,253.37 287.00,254.57 287.00,254.57 278.00,255.46 278.00,255.46 259.97,258.66 233.27,269.44 217.00,278.00 Z M 222.00,273.00 C 222.00,273.00 222.00,274.00 222.00,274.00 222.00,274.00 223.00,273.00 223.00,273.00 223.00,273.00 222.00,273.00 222.00,273.00 Z M 150.00,333.00 C 150.00,333.00 150.00,334.00 150.00,334.00 150.00,334.00 149.00,333.00 149.00,333.00 149.00,333.00 150.00,333.00 150.00,333.00 Z M 147.00,335.00 C 147.00,335.00 147.00,336.00 147.00,336.00 147.00,336.00 146.00,335.00 146.00,335.00 146.00,335.00 147.00,335.00 147.00,335.00 Z"
            />
            {this.toolTip(
              "LEFT_EYE_AND_LIDS",
              this.props.t("bodyMap.LEFT_EYE_AND_LIDS"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012145",
                  this.props.t("bodyMap.LEFT_UNDER_EYEBROW")
                )
              }
              id="UNDER_EYEBROW"
              class="left_eye_frt_3"
              d="M 102.00,223.00 C 102.00,223.00 105.00,225.00 105.00,225.00 96.30,229.29 92.47,236.08 89.34,245.00 87.29,250.85 84.58,258.78 85.18,265.00 86.44,278.26 100.38,293.55 112.00,298.96 115.85,300.75 121.75,301.89 126.00,301.99 135.49,302.20 146.71,299.26 155.00,294.65 155.00,294.65 165.00,288.20 165.00,288.20 165.00,288.20 181.00,279.38 181.00,279.38 181.00,279.38 193.00,271.05 193.00,271.05 193.00,271.05 210.00,261.57 210.00,261.57 221.15,255.19 231.89,248.08 244.00,243.47 244.00,243.47 262.00,239.44 262.00,239.44 262.00,239.44 285.00,227.77 285.00,227.77 305.36,221.50 326.96,221.97 348.00,222.00 348.00,222.00 363.00,223.00 363.00,223.00 363.00,223.00 391.00,225.00 391.00,225.00 405.34,225.07 406.86,226.66 420.00,229.87 420.00,229.87 433.00,233.03 433.00,233.03 444.88,236.80 442.12,238.03 457.00,240.00 457.00,240.00 456.00,243.00 456.00,243.00 456.00,243.00 461.00,244.00 461.00,244.00 463.81,240.59 468.20,242.22 472.00,243.30 472.00,243.30 501.00,250.29 501.00,250.29 501.00,250.29 522.00,256.03 522.00,256.03 522.00,256.03 536.00,261.26 536.00,261.26 550.45,265.94 557.99,267.39 572.00,274.78 572.00,274.78 596.00,289.42 596.00,289.42 601.85,293.40 612.22,300.37 619.00,301.69 629.87,303.80 634.20,300.41 643.00,294.96 646.50,292.80 652.29,290.00 654.95,287.22 661.65,280.19 660.74,270.68 656.00,263.00 656.00,263.00 660.00,265.00 660.00,265.00 655.95,260.35 643.62,252.29 638.00,248.29 620.11,235.55 601.07,224.04 582.00,213.15 546.98,193.16 509.82,176.08 471.00,164.91 471.00,164.91 444.00,158.50 444.00,158.50 444.00,158.50 424.00,154.29 424.00,154.29 407.45,151.74 390.71,151.00 374.00,151.00 374.00,151.00 352.00,151.00 352.00,151.00 352.00,151.00 334.00,152.72 334.00,152.72 334.00,152.72 319.00,154.27 319.00,154.27 319.00,154.27 280.00,162.73 280.00,162.73 241.57,172.87 205.70,189.49 169.00,204.31 147.71,212.90 125.33,223.00 102.00,223.00 Z M 454.00,243.00 C 454.00,243.00 454.00,244.00 454.00,244.00 454.00,244.00 455.00,243.00 455.00,243.00 455.00,243.00 454.00,243.00 454.00,243.00 Z"
            />
            {this.toolTip(
              "UNDER_EYEBROW",
              this.props.t("bodyMap.LEFT_UNDER_EYEBROW"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyRightEar() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 886"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="886"
              xlinkHref={require("../images/Ear(L) v1.png")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012118",
                  this.props.t("bodyMap.RIGHT_EAR_FLAP")
                )
              }
              id="RIGHT_EAR_FLAP"
              class="left_ear_frt_1"
              d="M 296.00,78.21
           C 296.00,78.21 264.00,82.66 264.00,82.66
             243.23,87.16 223.64,96.60 206.00,108.35
             144.76,149.18 115.31,223.25 98.13,292.00
             91.86,317.06 87.71,340.21 85.91,366.00
             85.91,366.00 85.00,376.00 85.00,376.00
             84.81,392.70 84.52,414.87 87.81,431.00
             89.85,441.05 94.29,450.93 104.00,455.60
             109.94,458.46 116.59,458.07 123.00,458.00
             132.59,457.88 149.13,453.60 159.00,451.42
             159.00,451.42 222.00,437.42 222.00,437.42
             222.00,437.42 435.00,389.42 435.00,389.42
             435.00,389.42 488.00,377.42 488.00,377.42
             488.00,377.42 531.00,367.28 531.00,367.28
             541.56,364.13 555.23,359.58 559.15,348.00
             560.21,344.87 560.04,340.34 560.00,337.00
             559.84,324.37 551.49,310.10 544.41,300.00
             544.41,300.00 499.69,241.00 499.69,241.00
             492.89,230.43 483.56,213.44 478.31,202.00
             467.38,178.21 463.02,162.42 447.28,140.00
             422.41,104.57 385.15,85.72 343.00,79.87
             343.00,79.87 325.00,78.21 325.00,78.21
             325.00,78.21 296.00,78.21 296.00,78.21 Z"
            />
            {this.toolTip(
              "RIGHT_EAR_FLAP",
              this.props.t("bodyMap.RIGHT_EAR_FLAP"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012174",
                  this.props.t("bodyMap.RIGHT_EARLOBE_AND_TUNNEL")
                )
              }
              id="RIGHT_EARLOBE_AND_TUNNEL"
              class="left_ear_frt_2"
              d="M 544.00,337.43
           C 528.01,340.05 513.11,346.32 498.00,351.95
             498.00,351.95 434.00,375.95 434.00,375.95
             434.00,375.95 224.00,453.31 224.00,453.31
             224.00,453.31 147.00,480.95 147.00,480.95
             134.97,485.44 119.28,491.48 112.48,503.00
             108.94,509.00 110.73,510.15 111.68,516.00
             111.68,516.00 112.56,526.00 112.56,526.00
             113.69,529.54 119.23,537.49 121.34,541.00
             124.30,545.91 123.48,548.28 124.58,550.91
             126.09,554.52 129.37,555.52 132.62,562.00
             135.42,567.56 135.41,572.52 137.15,576.00
             137.15,576.00 145.87,588.00 145.87,588.00
             145.87,588.00 163.00,610.00 163.00,610.00
             163.00,610.00 210.83,662.00 210.83,662.00
             240.19,694.50 278.65,735.29 312.00,763.08
             338.69,785.32 361.35,799.57 395.00,809.14
             404.18,811.75 417.54,814.89 427.00,815.00
             461.10,815.40 502.27,803.16 524.54,776.00
             544.09,752.16 552.76,720.73 562.33,692.00
             577.78,645.66 578.84,643.18 589.42,595.00
             592.85,579.38 597.98,555.63 598.00,540.00
             598.00,540.00 598.00,495.00 598.00,495.00
             598.00,495.00 597.00,475.00 597.00,475.00
             597.00,475.00 596.09,465.00 596.09,465.00
             596.09,465.00 588.08,403.00 588.08,403.00
             588.08,403.00 581.28,362.00 581.28,362.00
             575.72,343.49 563.14,335.06 544.00,337.43 Z"
            />

            {this.toolTip(
              "RIGHT_EARLOBE_AND_TUNNEL",
              this.props.t("bodyMap.RIGHT_EARLOBE_AND_TUNNEL"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012070",
                  this.props.t("bodyMap.BACK_OF_RIGHT_EAR")
                )
              }
              id="BACK_OF_RIGHT_EAR"
              class="left_ear_frt_3"
              d="M 165.00,93.53
           C 148.29,98.47 129.55,122.74 120.00,137.00
             120.00,137.00 94.14,182.00 94.14,182.00
             85.29,197.86 74.79,216.13 68.42,233.00
             68.42,233.00 56.58,274.00 56.58,274.00
             50.53,295.75 40.03,335.32 40.00,357.00
             40.00,357.00 40.00,407.00 40.00,407.00
             40.00,407.00 41.00,422.00 41.00,422.00
             41.29,446.97 46.88,465.19 52.35,489.00
             57.28,510.47 63.76,539.51 73.26,559.00
             80.89,574.66 107.10,617.68 117.80,631.00
             117.80,631.00 140.96,656.00 140.96,656.00
             140.96,656.00 172.00,688.00 172.00,688.00
             172.00,688.00 219.00,734.17 219.00,734.17
             219.00,734.17 293.00,799.13 293.00,799.13
             304.86,809.34 324.95,825.61 341.00,826.82
             351.62,827.62 360.54,821.24 359.96,810.00
             359.44,800.11 348.27,792.43 341.00,786.88
             341.00,786.88 288.00,741.17 288.00,741.17
             288.00,741.17 252.09,706.00 252.09,706.00
             252.09,706.00 221.96,673.00 221.96,673.00
             221.96,673.00 190.01,641.00 190.01,641.00
             190.01,641.00 180.96,631.00 180.96,631.00
             180.96,631.00 170.29,620.00 170.29,620.00
             156.87,604.70 140.64,579.85 130.43,562.00
             111.43,528.79 98.68,492.40 91.20,455.00
             88.51,441.55 85.16,424.62 84.96,411.00
             84.96,411.00 84.96,400.00 84.96,400.00
             84.96,400.00 84.00,390.00 84.00,390.00
             84.00,390.00 85.17,353.00 85.17,353.00
             85.17,353.00 92.00,315.00 92.00,315.00
             97.03,290.69 102.86,267.29 111.58,244.00
             126.16,205.00 130.95,193.09 153.42,157.00
             153.42,157.00 181.73,117.00 181.73,117.00
             185.32,109.63 185.69,98.05 176.96,94.09
             173.37,92.46 168.78,92.91 165.00,93.53 Z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_EAR",
              this.props.t("bodyMap.BACK_OF_RIGHT_EAR"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyLeftEar() {
    return (
      <div style={{ paddingTop: 130 }}>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 886"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="886"
              xlinkHref={require("../images/Ear(L) v1.png")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012107",
                  this.props.t("bodyMap.LEFT_EAR_FLAP")
                )
              }
              id="LEFT_EAR_FLAP"
              class="left_ear_frt_1"
              d="M 296.00,78.21
          C 296.00,78.21 264.00,82.66 264.00,82.66
            243.23,87.16 223.64,96.60 206.00,108.35
            144.76,149.18 115.31,223.25 98.13,292.00
            91.86,317.06 87.71,340.21 85.91,366.00
            85.91,366.00 85.00,376.00 85.00,376.00
            84.81,392.70 84.52,414.87 87.81,431.00
            89.85,441.05 94.29,450.93 104.00,455.60
            109.94,458.46 116.59,458.07 123.00,458.00
            132.59,457.88 149.13,453.60 159.00,451.42
            159.00,451.42 222.00,437.42 222.00,437.42
            222.00,437.42 435.00,389.42 435.00,389.42
            435.00,389.42 488.00,377.42 488.00,377.42
            488.00,377.42 531.00,367.28 531.00,367.28
            541.56,364.13 555.23,359.58 559.15,348.00
            560.21,344.87 560.04,340.34 560.00,337.00
            559.84,324.37 551.49,310.10 544.41,300.00
            544.41,300.00 499.69,241.00 499.69,241.00
            492.89,230.43 483.56,213.44 478.31,202.00
            467.38,178.21 463.02,162.42 447.28,140.00
            422.41,104.57 385.15,85.72 343.00,79.87
            343.00,79.87 325.00,78.21 325.00,78.21
            325.00,78.21 296.00,78.21 296.00,78.21 Z"
            />
            {this.toolTip(
              "LEFT_EAR_FLAP",
              this.props.t("bodyMap.LEFT_EAR_FLAP"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012135",
                  this.props.t("bodyMap.LEFT_EARLOBE_AND_TUNNEL")
                )
              }
              id="LEFT_EARLOBE_AND_TUNNEL"
              class="left_ear_frt_2"
              d="M 544.00,337.43
          C 528.01,340.05 513.11,346.32 498.00,351.95
            498.00,351.95 434.00,375.95 434.00,375.95
            434.00,375.95 224.00,453.31 224.00,453.31
            224.00,453.31 147.00,480.95 147.00,480.95
            134.97,485.44 119.28,491.48 112.48,503.00
            108.94,509.00 110.73,510.15 111.68,516.00
            111.68,516.00 112.56,526.00 112.56,526.00
            113.69,529.54 119.23,537.49 121.34,541.00
            124.30,545.91 123.48,548.28 124.58,550.91
            126.09,554.52 129.37,555.52 132.62,562.00
            135.42,567.56 135.41,572.52 137.15,576.00
            137.15,576.00 145.87,588.00 145.87,588.00
            145.87,588.00 163.00,610.00 163.00,610.00
            163.00,610.00 210.83,662.00 210.83,662.00
            240.19,694.50 278.65,735.29 312.00,763.08
            338.69,785.32 361.35,799.57 395.00,809.14
            404.18,811.75 417.54,814.89 427.00,815.00
            461.10,815.40 502.27,803.16 524.54,776.00
            544.09,752.16 552.76,720.73 562.33,692.00
            577.78,645.66 578.84,643.18 589.42,595.00
            592.85,579.38 597.98,555.63 598.00,540.00
            598.00,540.00 598.00,495.00 598.00,495.00
            598.00,495.00 597.00,475.00 597.00,475.00
            597.00,475.00 596.09,465.00 596.09,465.00
            596.09,465.00 588.08,403.00 588.08,403.00
            588.08,403.00 581.28,362.00 581.28,362.00
            575.72,343.49 563.14,335.06 544.00,337.43 Z"
            />
            {this.toolTip(
              "LEFT_EARLOBE_AND_TUNNEL",
              this.props.t("bodyMap.LEFT_EARLOBE_AND_TUNNEL"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012054",
                  this.props.t("bodyMap.BACK_OF_LEFT_EAR")
                )
              }
              id="BACK_OF_LEFT_EAR"
              class="left_ear_frt_3"
              d="M 165.00,93.53
          C 148.29,98.47 129.55,122.74 120.00,137.00
            120.00,137.00 94.14,182.00 94.14,182.00
            85.29,197.86 74.79,216.13 68.42,233.00
            68.42,233.00 56.58,274.00 56.58,274.00
            50.53,295.75 40.03,335.32 40.00,357.00
            40.00,357.00 40.00,407.00 40.00,407.00
            40.00,407.00 41.00,422.00 41.00,422.00
            41.29,446.97 46.88,465.19 52.35,489.00
            57.28,510.47 63.76,539.51 73.26,559.00
            80.89,574.66 107.10,617.68 117.80,631.00
            117.80,631.00 140.96,656.00 140.96,656.00
            140.96,656.00 172.00,688.00 172.00,688.00
            172.00,688.00 219.00,734.17 219.00,734.17
            219.00,734.17 293.00,799.13 293.00,799.13
            304.86,809.34 324.95,825.61 341.00,826.82
            351.62,827.62 360.54,821.24 359.96,810.00
            359.44,800.11 348.27,792.43 341.00,786.88
            341.00,786.88 288.00,741.17 288.00,741.17
            288.00,741.17 252.09,706.00 252.09,706.00
            252.09,706.00 221.96,673.00 221.96,673.00
            221.96,673.00 190.01,641.00 190.01,641.00
            190.01,641.00 180.96,631.00 180.96,631.00
            180.96,631.00 170.29,620.00 170.29,620.00
            156.87,604.70 140.64,579.85 130.43,562.00
            111.43,528.79 98.68,492.40 91.20,455.00
            88.51,441.55 85.16,424.62 84.96,411.00
            84.96,411.00 84.96,400.00 84.96,400.00
            84.96,400.00 84.00,390.00 84.00,390.00
            84.00,390.00 85.17,353.00 85.17,353.00
            85.17,353.00 92.00,315.00 92.00,315.00
            97.03,290.69 102.86,267.29 111.58,244.00
            126.16,205.00 130.95,193.09 153.42,157.00
            153.42,157.00 181.73,117.00 181.73,117.00
            185.32,109.63 185.69,98.05 176.96,94.09
            173.37,92.46 168.78,92.91 165.00,93.53 Z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_EAR",
              this.props.t("bodyMap.BACK_OF_LEFT_EAR"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyFrightHand() {
    return (
      <div style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("bLeftHand")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 923"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="923"
              xlinkHref={require("../images/Front-Of-Hand(R) v1.jpg")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012166",
                  this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_THUMB")
                )
              }
              id="PALM_SIDE_OF_THUMB"
              class="right_hand_frt_1"
              d="M 695 410 C 700 350 605 350 560 430 C 550 455 525 475 510 495 L 595 565 C 600 515 610 465 695 410 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_THUMB",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_THUMB"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012162",
                  this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_INDEX_FINGER")
                )
              }
              id="PALM_SIDE_OF_RIGHT_INDEX_FINGER"
              class="right_hand_frt_2"
              d="M 510 140 C 525 75 450 75 450 135 C 435 200 425 300 405 340 L 490 365 C 500 245 505 195 510 140 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_RIGHT_INDEX_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_INDEX_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012163",
                  this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_MIDDLE_FINGER")
                )
              }
              id="PALM_SIDE_OF_RIGHT_MIDDLE_FINGER"
              class="right_hand_frt_3"
              d="M 355 90 C 350 45 275 60 290 90 C 295 190 305 225 315 340 L 390 340 C 375 215 365 180 355 90 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_RIGHT_MIDDLE_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_MIDDLE_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012165",
                  this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_RING_FINGER")
                )
              }
              id="PALM_SIDE_OF_RIGHT_RING_FINGER"
              class="right_hand_frt_4"
              d="M 220 145 C 215 115 145 135 165 170 C 180 210 185 230 245 395 L 310 345 C 260 235 240 200 220 145 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_RIGHT_RING_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_RING_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012164",
                  this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_PINKY")
                )
              }
              id="PALM_SIDE_OF_PINKY"
              class="right_hand_frt_5"
              d="M 85 290 C 45 260 25 290 50 320 C 85 365 160 425 180 475 L 245 400 C 205 390 145 345 85 285 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_PINKY",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_PINKY"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012143",
                  this.props.t("bodyMap.RIGHT_PALM")
                )
              }
              id="PALM"
              class="right_hand_frt_6"
              d="M 245 435 C 335 395 400 370 465 400 C 480 480 515 505 525 620 C 455 645 425 655 265 665 C 230 605 205 495 200 450 Z"
            />
            {this.toolTip("PALM", this.props.t("bodyMap.RIGHT_PALM"), "left")}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyFleftHand() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("bRightHand")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 923"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="923"
              xlinkHref={require("../images/Front-Of-Hand(R) v1.jpg")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012160",
                  this.props.t("bodyMap.PALM_SIDE_OF_LEFT_THUMB")
                )
              }
              id="PALM_SIDE_OF_THUMB"
              class="right_hand_frt_1"
              d="M 695 410 C 700 350 605 350 560 430 C 550 455 525 475 510 495 L 595 565 C 600 515 610 465 695 410 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_THUMB",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_THUMB"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012156",
                  this.props.t("bodyMap.PALM_SIDE_OF_LEFT_INDEX_FINGER")
                )
              }
              id="PALM_SIDE_OF_LEFT_INDEX_FINGER"
              class="right_hand_frt_2"
              d="M 510 140 C 525 75 450 75 450 135 C 435 200 425 300 405 340 L 490 365 C 500 245 505 195 510 140 Z"
            />

            {this.toolTip(
              "PALM_SIDE_OF_LEFT_INDEX_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_INDEX_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012157",
                  this.props.t("bodyMap.PALM_SIDE_OF_LEFT_MIDDLE_FINGER")
                )
              }
              id="PALM_SIDE_OF_LEFT_MIDDLE_FINGER"
              class="right_hand_frt_3"
              d="M 355 90 C 350 45 275 60 290 90 C 295 190 305 225 315 340 L 390 340 C 375 215 365 180 355 90 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_LEFT_MIDDLE_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_MIDDLE_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012159",
                  this.props.t("bodyMap.PALM_SIDE_OF_LEFT_RING_FINGER")
                )
              }
              id="PALM_SIDE_OF_LEFT_RING_FINGER"
              class="right_hand_frt_4"
              d="M 220 145 C 215 115 145 135 165 170 C 180 210 185 230 245 395 L 310 345 C 260 235 240 200 220 145 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_LEFT_RING_FINGER",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_RING_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012158",
                  this.props.t("bodyMap.PALM_SIDE_OF_LEFT_PINKY")
                )
              }
              id="PALM_SIDE_OF_PINKY"
              class="right_hand_frt_5"
              d="M 85 290 C 45 260 25 290 50 320 C 85 365 160 425 180 475 L 245 400 C 205 390 145 345 85 285 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_PINKY",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_PINKY"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012143",
                  this.props.t("bodyMap.LEFT_PALM")
                )
              }
              id="PALM"
              class="right_hand_frt_6"
              d="M 245 435 C 335 395 400 370 465 400 C 480 480 515 505 525 620 C 455 645 425 655 265 665 C 230 605 205 495 200 450 Z"
            />
            {this.toolTip("PALM", this.props.t("bodyMap.LEFT_PALM"), "right")}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyFrightFoot() {
    return (
      <div style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("bLeftFoot")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 1061"
            xmlSpace="preserve"
          >
            {/* ..... */}
            <image
              overflow="visible"
              width="740"
              height="1061"
              xlinkHref={require("../images/Front-Of-Foot(R) v1.jpg")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012197",
                  this.props.t("bodyMap.TOP_OF_RIGHT_BIG_TOE")
                )
              }
              id="TOP_OF_RIGHT_BIG_TOE"
              class="right_foot_frt_1"
              d="M 65 980 C 90 1055 225 1050 245 980 C 265 920 230 835 220 805 C 180 745 125 740 65 795 C 75 865 40 925 65 980 Z"
            />
            {this.toolTip(
              "TOP_OF_RIGHT_BIG_TOE",
              this.props.t("bodyMap.TOP_OF_RIGHT_BIG_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012200",
                  this.props.t("bodyMap.TOP_OF_RIGHT_INDEX_TOE")
                )
              }
              id="TOP_OF_RIGHT_INDEX_TOE"
              class="right_foot_frt_2"
              d="M 250 975 C 245 1060 365 1060 360 970 C 360 915 370 875 360 845 C 355 830 275 815 265 840 C 265 880 255 935 250 975 Z"
            />
            {this.toolTip(
              "TOP_OF_RIGHT_INDEX_TOE",
              this.props.t("bodyMap.TOP_OF_RIGHT_INDEX_TOE"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012202",
                  this.props.t("bodyMap.TOP_OF_RIGHT_MIDDLE_TOE")
                )
              }
              id="TOP_OF_RIGHT_MIDDLE_TOE"
              class="right_foot_frt_3"
              d="M 375 970 C 360 1035 470 1045 485 970 C 495 910 500 855 465 830 C 435 810 395 820 380 845 C 385 880 385 915 375 970 Z"
            />
            {this.toolTip(
              "TOP_OF_RIGHT_MIDDLE_TOE",
              this.props.t("bodyMap.TOP_OF_RIGHT_MIDDLE_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012199",
                  this.props.t("bodyMap.TOP_OF_RIGHT_FOURTH_TOE")
                )
              }
              id="TOP_OF_RIGHT_FOURTH_TOE"
              class="right_foot_frt_4"
              d="M 495 940 C 500 965 620 955 600 915 C 615 895 610 815 590 780 C 560 730 490 740 500 790 C 490 885 495 910 495 940 Z"
            />
            {this.toolTip(
              "TOP_OF_RIGHT_FOURTH_TOE",
              this.props.t("bodyMap.TOP_OF_RIGHT_FOURTH_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012201",
                  this.props.t("bodyMap.TOP_OF_RIGHT_LITTLE_TOE")
                )
              }
              id="TOP_OF_RIGHT_LITTLE_TOE"
              class="right_foot_frt_5"
              d="M 605 850 C 665 915 710 830 695 790 C 700 750 675 710 660 685 C 625 655 580 675 560 735 C 575 760 585 790 605 845 Z"
            />
            {this.toolTip(
              "TOP_OF_RIGHT_LITTLE_TOE",
              this.props.t("bodyMap.TOP_OF_RIGHT_LITTLE_TOE"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012198",
                  this.props.t("bodyMap.TOP_OF_RIGHT_FOOT")
                )
              }
              id="FOOT_TOP_OF_RIGHT_FOOT"
              class="right_foot_frt_6"
              d="M 510 95 C 475 435 575 385 680 710 C 680 710 680 710 680 710 C 530 785 270 785 80 730 C 165 605 140 275 140 95 Z"
            />
            {this.toolTip(
              "FOOT_TOP_OF_RIGHT_FOOT",
              this.props.t("bodyMap.TOP_OF_RIGHT_FOOT"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyFleftFoot() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("bRightFoot")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="100%"
            viewBox="0 0 740 1061"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="1061"
              xlinkHref={require("../images/Front-Of-Foot(R) v1.jpg")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012191",
                  this.props.t("bodyMap.TOP_OF_LEFT_BIG_TOE")
                )
              }
              id="TOP_OF_LEFT_BIG_TOE"
              class="right_foot_frt_1"
              d="M 65 980 C 90 1055 225 1050 245 980 C 265 920 230 835 220 805 C 180 745 125 740 65 795 C 75 865 40 925 65 980 Z"
            />
            {this.toolTip(
              "TOP_OF_LEFT_BIG_TOE",
              this.props.t("bodyMap.TOP_OF_LEFT_BIG_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012194",
                  this.props.t("bodyMap.TOP_OF_LEFT_INDEX_TOE")
                )
              }
              id="TOP_OF_LEFT_INDEX_TOE"
              class="right_foot_frt_2"
              d="M 250 975 C 245 1060 365 1060 360 970 C 360 915 370 875 360 845 C 355 830 275 815 265 840 C 265 880 255 935 250 975 Z"
            />

            {this.toolTip(
              "TOP_OF_LEFT_INDEX_TOE",
              this.props.t("bodyMap.TOP_OF_LEFT_INDEX_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012196",
                  this.props.t("bodyMap.TOP_OF_LEFT_MIDDLE_TOE")
                )
              }
              id="TOP_OF_LEFT_MIDDLE_TOE"
              class="right_foot_frt_3"
              d="M 375 970 C 360 1035 470 1045 485 970 C 495 910 500 855 465 830 C 435 810 395 820 380 845 C 385 880 385 915 375 970 Z"
            />

            {this.toolTip(
              "TOP_OF_LEFT_MIDDLE_TOE",
              this.props.t("bodyMap.TOP_OF_LEFT_MIDDLE_TOE"),
              "bottom"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012193",
                  this.props.t("bodyMap.TOP_OF_LEFT_FOURTH_TOE")
                )
              }
              id="TOP_OF_LEFT_FOURTH_TOE"
              class="right_foot_frt_4"
              d="M 495 940 C 500 965 620 955 600 915 C 615 895 610 815 590 780 C 560 730 490 740 500 790 C 490 885 495 910 495 940 Z"
            />

            {this.toolTip(
              "TOP_OF_LEFT_FOURTH_TOE",
              this.props.t("bodyMap.TOP_OF_LEFT_FOURTH_TOE"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012195",
                  this.props.t("bodyMap.TOP_OF_LEFT_LITTLE_TOE")
                )
              }
              id="TOP_OF_LEFT_LITTLE_TOE"
              class="right_foot_frt_5"
              d="M 605 850 C 665 915 710 830 695 790 C 700 750 675 710 660 685 C 625 655 580 675 560 735 C 575 760 585 790 605 845 Z"
            />

            {this.toolTip(
              "TOP_OF_LEFT_LITTLE_TOE",
              this.props.t("bodyMap.TOP_OF_LEFT_LITTLE_TOE"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012192",
                  this.props.t("bodyMap.TOP_OF_LEFT_FOOT")
                )
              }
              id="FOOT_TOP_OF_LEFT_FOOT"
              class="right_foot_frt_6"
              d="M 510 95 C 475 435 575 385 680 710 C 680 710 680 710 680 710 C 530 785 270 785 80 730 C 165 605 140 275 140 95 Z"
            />
            {this.toolTip(
              "FOOT_TOP_OF_LEFT_FOOT",
              this.props.t("bodyMap.TOP_OF_LEFT_FOOT"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBrightFoot() {
    return (
      <div style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("fLeftFoot")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="500px"
            viewBox="0 0 740 1777"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="1777"
              xlinkHref={require("../images/Bottom-Of-Foot(L) v1.jpg")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012087",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_BIG_TOE")
                )
              }
              id="BOTTOM_OF_LEFT_BIG_TOE"
              class="left_foot_frt_1"
              d="M 102 330 C 84 216 84 156 144 81 C 171 33 285 9 300 81 C 357 162 267 264 282 333 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_LEFT_BIG_TOE",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_BIG_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012090",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_INDEX_TOE")
                )
              }
              id="BOTTOM_OF_LEFT_INDEX_TOE"
              class="left_foot_frt_2"
              d="M 315 315 C 351 228 351 168 330 153 C 294 21 471 33 441 123 C 405 195 432 234 399 297 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_LEFT_INDEX_TOE",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_INDEX_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012092",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_MIDDLE_TOE")
                )
              }
              id="BOTTOM_OF_LEFT_MIDDLE_TOE"
              class="left_foot_frt_3"
              d="M 498 324 C 507 234 519 204 528 138 C 531 93 459 51 441 123 C 405 195 432 234 399 297 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_LEFT_MIDDLE_TOE",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_MIDDLE_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012089",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_FOURTH_TOE")
                )
              }
              id="BOTTOM_OF_LEFT_FOURTH_TOE"
              class="left_foot_frt_4"
              d="M 495 333 C 513 285 513 228 516 180 C 522 135 588 144 594 186 C 621 225 582 327 585 390 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_LEFT_FOURTH_TOE",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_FOURTH_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012091",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_LITTLE_TOE")
                )
              }
              id="BOTTOM_OF_LEFT_LITTLE_TOE"
              class="left_foot_frt_5"
              d="M 672 426 C 690 375 693 330 669 294 C 654 252 609 264 594 294 C 591 312 582 327 585 390 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_LEFT_LITTLE_TOE",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_LITTLE_TOE"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012088",
                  this.props.t("bodyMap.BOTTOM_OF_LEFT_FOOT")
                )
              }
              id="FOOT_BOTTOM_OF_LEFT_FOOT"
              class="left_foot_frt_6"
              d="M 669 441 C 273 450 420 408 81 456 C 6 558 165 1086 159 1389 C 84 1777 474 1776 534 1680 C 609 1611 738 585 669 441 Z"
            />
            {this.toolTip(
              "FOOT_BOTTOM_OF_LEFT_FOOT",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_FOOT"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBleftFoot() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("fRightFoot")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740px"
            height="500px"
            viewBox="0 0 740 1777"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              height="1777"
              xlinkHref={require("../images/Bottom-Of-Foot(L) v1.jpg")}
            ></image>
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012093",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_BIG_TOE")
                )
              }
              id="BOTTOM_OF_RIGHT_BIG_TOE"
              class="left_foot_frt_1"
              d="M 102 330 C 84 216 84 156 144 81 C 171 33 285 9 300 81 C 357 162 267 264 282 333 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_RIGHT_BIG_TOE",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_BIG_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012096",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_INDEX_TOE")
                )
              }
              id="BOTTOM_OF_RIGHT_INDEX_TOE"
              class="left_foot_frt_2"
              d="M 315 315 C 351 228 351 168 330 153 C 294 21 471 33 441 123 C 405 195 432 234 399 297 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_RIGHT_INDEX_TOE",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_INDEX_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012098",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_MIDDLE_TOE")
                )
              }
              id="BOTTOM_OF_RIGHT_MIDDLE_TOE"
              class="left_foot_frt_3"
              d="M 498 324 C 507 234 519 204 528 138 C 531 93 459 51 441 123 C 405 195 432 234 399 297 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_RIGHT_MIDDLE_TOE",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_MIDDLE_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012095",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_FOURTH_TOE")
                )
              }
              id="BOTTOM_OF_RIGHT_FOURTH_TOE"
              class="left_foot_frt_4"
              d="M 495 333 C 513 285 513 228 516 180 C 522 135 588 144 594 186 C 621 225 582 327 585 390 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_RIGHT_FOURTH_TOE",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_FOURTH_TOE"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012097",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_LITTLE_TOE")
                )
              }
              id="BOTTOM_OF_RIGHT_LITTLE_TOE"
              class="left_foot_frt_5"
              d="M 672 426 C 690 375 693 330 669 294 C 654 252 609 264 594 294 C 591 312 582 327 585 390 Z"
            />
            {this.toolTip(
              "BOTTOM_OF_RIGHT_LITTLE_TOE",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_LITTLE_TOE"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012094",
                  this.props.t("bodyMap.BOTTOM_OF_RIGHT_FOOT")
                )
              }
              id="FOOT_BOTTOM_OF_RIGHT_FOOT"
              class="left_foot_frt_6"
              d="M 669 441 C 273 450 420 408 81 456 C 6 558 165 1086 159 1389 C 84 1777 474 1776 534 1680 C 609 1611 738 585 669 441 Z"
            />
            {this.toolTip(
              "FOOT_BOTTOM_OF_RIGHT_FOOT",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_FOOT"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBLeftHand() {
    return (
      <div style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("fLeftHand")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="768px"
            height="100%"
            viewBox="0 0 768 768"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="768"
              height="768"
              xlinkHref={require("../images/Back-Of-Hand(L) v2.jpg")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012061",
                  this.props.t("bodyMap.BACK_OF_LEFT_PINKY")
                )
              }
              id="BACK_OF_PINKY"
              class="left_hand_frt_5"
              d="m 166.85664,419.04288 c 0,0 19.52686,-3.59116 30.74918,-12.56901 11.22235,-8.97786 12.12013,-19.3024 12.56903,-25.81138 0.4489,-6.50895 0.89778,-19.52683 -0.67333,-22.6691 -1.57112,-3.14226 -1.44799,-2.75186 -1.44799,-2.75186 l -4.73909,-1.13424 c 0,0 -4.99772,-1.2384 -6.32455,-1.72489 -1.32682,-0.48649 -7.78409,-3.22863 -7.78409,-3.22863 l -4.42277,-2.29981 -4.73235,-3.00749 -5.66115,-4.15742 -10.48195,-8.31481 -7.56295,-6.28032 -16.49692,-14.24131 -9.42047,-8.53596 -5.21889,-5.35153 -3.3613,-4.33432 -3.89202,-4.86504 -5.52847,-6.63414 c 0,0 -3.58243,-4.02473 -4.06895,-4.46701 -0.4865,-0.44228 -4.90925,-4.9535 -4.90925,-4.9535 l -8.37992,-7.46985 -7.109359,-4.94867 -6.412363,-3.6244 c 0,0 -4.948676,-1.95156 -6.412366,-2.36979 -1.463688,-0.41818 -3.903176,-0.76667 -3.903176,-0.76667 l -5.436566,-0.2091 c 0,0 -3.066787,0.2091 -4.669888,0.76669 -1.603083,0.55761 -4.600164,2.0213 -5.157761,2.50918 -0.557597,0.48789 -2.50919,1.9516 -2.50919,1.9516 l -1.46369,1.6031 -1.463703,2.43947 -1.115192,2.64859 -0.418188,2.78799 c 0,0 -0.209098,2.43948 -0.139408,3.13651 0.06971,0.69697 0.4182,3.13645 0.697006,3.76375 0.278791,0.62729 1.115193,3.06678 1.115193,3.06678 0,0 2.997081,6.20328 3.415282,6.96997 0.418187,0.7667 2.439487,3.69408 2.439487,3.69408 l 12.058034,16.10061 13.521722,17.77341 6.76086,8.64275 9.339757,11.98833 11.151939,14.91572 10.78422,13.93205 11.04145,12.51368 9.2538,9.14862 z"
            />
            {this.toolTip(
              "BACK_OF_PINKY",
              this.props.t("bodyMap.BACK_OF_LEFT_PINKY"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012062",
                  this.props.t("bodyMap.BACK_OF_LEFT_RING_FINGER")
                )
              }
              id="BACK_OF_LEFT_RING_FINGER"
              class="left_hand_frt_4"
              d="m 226.15118,343.65935 c 54.98491,8.29961 68.67927,-38.80068 68.67927,-38.80068 l -3.38695,-4.54845 -6.98781,-11.21897 -8.65463,-15.45012 -7.18014,-13.46277 -9.61626,-20.51466 -5.44921,-11.41131 -3.39774,-7.693 -7.50067,-16.98872 -4.16707,-9.29572 -5.96207,-14.80904 c 0,0 -1.73092,-5.00045 -2.11558,-6.47493 -0.38464,-1.4745 -3.39774,-11.79595 -3.39774,-11.79595 l -3.46185,-11.79594 -3.97472,-12.88578 c 0,0 -3.52597,-10.57789 -3.91061,-11.28308 -0.38465,-0.70519 -2.43612,-6.0903 -2.43612,-6.0903 l -3.33363,-7.56479 -2.94899,-5.70566 c 0,0 -2.82076,-4.48759 -3.46184,-5.25687 -0.64109,-0.769305 -3.39776,-3.525975 -3.39776,-3.525975 l -2.24379,-1.730908 -2.62845,-1.73093 c 0,0 -2.24379,-0.89752 -3.20541,-1.282165 -0.96162,-0.384662 -4.29526,-1.153954 -4.29526,-1.153954 0,0 -3.14131,-0.128204 -4.10295,-0.192331 -0.96162,-0.0642 -5.32098,0.448756 -5.32098,0.448756 0,0 -2.11557,0.448764 -3.26954,1.025741 -1.15395,0.576977 -2.11558,0.89752 -3.26952,1.666811 -1.15395,0.769308 -2.24379,1.218072 -3.20541,2.2438 -0.96164,1.02573 -2.75667,3.20542 -2.949,3.718265 -0.19233,0.51289 -1.02574,2.05148 -1.21805,2.69257 -0.19233,0.64107 -0.6411,1.60269 -0.89752,2.75665 -0.25643,1.15396 -0.38465,3.20543 -0.38465,4.61582 0,1.41038 -0.19233,3.9106 -0.0642,6.41083 0.1282,2.50024 0.51286,7.11604 0.64107,8.52643 0.12821,1.41038 1.15396,8.97517 1.41041,11.41129 0.25642,2.43612 2.24378,12.50112 2.24378,12.50112 l 2.56434,13.01401 3.85933,15.73052 6.04563,23.9761 c 0,0 5.52326,21.87651 5.73987,23.01367 0.2166,1.13713 3.51973,13.48328 4.33198,16.62397 0.81225,3.1407 4.60273,17.00302 4.60273,17.00302 0,0 3.51973,12.6169 4.16954,14.99949 0.64978,2.3826 2.70747,8.60981 2.70747,8.60981 0,0 2.166,5.79404 2.59919,6.87702 0.43321,1.083 3.41145,7.74342 3.41145,7.74342 0,0 4.33198,9.31376 4.71102,10.39676 0.37905,1.083 3.89878,8.77226 4.38613,10.01769 0.48734,1.24545 3.51973,7.90588 3.95293,9.15132 0.43321,1.24545 3.03239,8.01416 3.03239,8.01416 l 1.7328,4.332 c 0,0 0.86639,3.14067 0.86639,3.79047 0,0.6498 0.10833,1.35149 0.10833,1.35149 z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_RING_FINGER",
              this.props.t("bodyMap.BACK_OF_LEFT_RING_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012060",
                  this.props.t("bodyMap.BACK_OF_LEFT_MIDDLE_FINGER")
                )
              }
              id="BACK_OF_LEFT_MIDDLE_FINGER"
              class="left_hand_frt_3"
              d="m 305.84655,302.65337 c 45.10107,20.56078 77.82145,-15.47586 77.82145,-15.47586 l -4.20058,-19.23428 -3.75843,-23.65596 -5.30601,-30.73062 -4.70807,-21.84737 -3.31644,-12.54482 -3.46064,-11.67967 -4.32581,-14.13094 -5.47935,-19.7545 -2.45128,-10.09353 -3.60484,-15.4287 -1.58612,-7.49805 -1.00936,-6.056125 -1.87451,-8.219021 -3.02806,-12.256436 c 0,0 -0.57678,-3.028063 -3.60483,-10.381918 -3.02806,-7.353869 -6.20033,-12.977411 -7.64225,-15.140307 -1.44194,-2.162899 -5.33516,-6.488699 -5.33516,-6.488699 l -4.46999,-4.03742 c 0,0 -2.59548,-1.73032 -4.3258,-2.59549 -1.73033,-0.86515 -3.74903,-1.29773 -6.20032,-1.58612 -2.45129,-0.28838 -6.77709,0.43258 -7.93064,0.57677 -1.15354,0.1442 -5.91192,1.58613 -6.4887,2.01871 -0.57677,0.43258 -2.88386,1.87452 -3.60483,2.3071 -0.72097,0.43258 -3.60484,3.31645 -4.47,4.90257 -0.86516,1.58613 -3.31645,6.777091 -3.60483,8.795801 -0.28838,2.018705 -0.86516,8.795791 -0.86516,10.526111 0,1.730326 0.43258,9.084188 0.43258,9.084188 l 2.88387,17.303203 2.88387,15.861263 2.88386,22.63837 3.17226,28.83867 1.87451,22.49417 2.88387,27.54094 4.3258,38.35543 4.1816,26.81997 c 0,0 2.16291,14.27514 2.16291,16.58224 0,2.30709 0.86516,8.79579 0.86516,9.94934 0,1.15354 0.28027,4.24099 0.28027,4.24099 z"            
            />
            {this.toolTip(
              "BACK_OF_LEFT_MIDDLE_FINGER",
              this.props.t("bodyMap.BACK_OF_LEFT_MIDDLE_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012057",
                  this.props.t("bodyMap.BACK_OF_LEFT_INDEX_FINGER")
                )
              }
              id="BACK_OF_LEFT_INDEX_FINGER"
              class="left_hand_frt_2"
              d="m 411.3237,277.46487 c 26.54877,43.78218 76.118,6.06759 76.118,6.06759 l 1.84474,-7.8492 2.67271,-12.24165 3.43196,-18.04372 c 0,0 1.87014,-11.30208 2.60192,-20.08354 0.73182,-8.78148 1.30095,-20.73403 1.30095,-20.73403 l 0.81314,-16.83116 0.48786,-16.26198 0.56914,-30.73514 0.40656,-28.62109 0.32526,-9.10671 c 0,0 -0.16265,-4.228112 -0.48787,-6.667413 -0.32526,-2.439292 -1.13834,-7.643124 -1.38226,-8.374919 -0.24396,-0.731786 -2.2767,-6.260861 -2.60191,-6.667409 -0.32526,-0.406552 -1.95149,-3.740258 -3.33375,-5.285146 -1.38226,-1.544887 -2.84582,-3.496326 -5.36643,-4.797283 -2.5206,-1.300959 -3.17109,-1.788822 -5.61039,-2.357989 -2.43931,-0.569174 -3.17109,-1.057029 -6.34218,-1.057029 -3.17108,0 -3.98418,-0.406552 -6.58609,0.406554 -2.60191,0.813097 -3.3337,0.894408 -4.95991,2.114056 -1.62619,1.219647 -3.98419,3.415013 -3.98419,3.415013 0,0 -1.38225,1.138339 -2.76451,3.171089 -1.38231,2.032747 -3.00848,5.041214 -3.00848,5.041214 l -1.5449,3.496327 -2.05161,5.674964 -3.42951,13.718121 -2.28636,17.86213 -0.88973,11.60272 c 0,0 -0.2684,4.16039 -0.80522,7.15766 -0.53684,2.99727 -1.25261,8.7234 -1.87889,11.49699 -0.62631,2.77359 -0.71576,5.23404 -1.65522,9.17075 -0.93943,3.93671 -1.29732,6.21822 -1.92361,8.7234 -0.62631,2.50517 -2.14731,9.4839 -2.14731,9.4839 l -1.83414,8.8576 -2.54992,11.8996 -1.74469,8.63393 -1.69993,8.45499 z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_INDEX_FINGER",
              this.props.t("bodyMap.BACK_OF_LEFT_INDEX_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012064",
                  this.props.t("bodyMap.BACK_OF_LEFT_THUMB")
                )
              }
              id="BACK_OF_THUMB"
              class="left_hand_frt_1"
              d="m 688.27301,362.88308 0.009,0.005 c -4.06912,0.0668 -9.20499,0.66685 -12.27348,1.26721 -3.06853,0.60035 -10.53961,2.00094 -15.14237,3.6019 -4.60277,1.60095 -13.14142,4.66948 -16.20993,6.20373 -3.06847,1.53425 -7.73813,4.06913 -10.80661,6.20374 -3.06851,2.13461 -7.00404,4.73646 -10.53949,8.00509 -3.53548,3.26863 -4.06885,3.60163 -5.93663,5.66954 -1.86777,2.06791 -3.93606,4.73701 -5.27018,6.6048 -1.33414,1.8678 -2.73472,4.26875 -3.2017,4.8691 -0.46691,0.60037 -0.66725,0.20049 -4.60289,3.33569 -3.9357,3.13522 -9.60595,7.20385 -9.60595,7.20385 l -6.00316,4.73598 c 0,0 -3.13533,2.60187 -7.33786,6.07061 -4.20251,3.46875 -6.7156,5.67315 -8.88316,7.59381 -2.16763,1.92065 -4.11562,3.53936 -6.2558,5.37769 -2.14015,1.83835 -3.21018,2.93631 -4.99365,4.36309 -1.78347,1.42678 -4.33518,3.70429 -6.06385,4.96642 -1.72857,1.26212 -2.05799,1.61883 -4.41767,3.21019 -2.35965,1.59145 -3.07329,2.2771 -5.29579,3.45686 -2.02192,1.07343 -4.65595,2.12402 -5.11486,2.30485 -0.0198,0.014 -0.0407,0.0271 -0.0622,0.0393 0,2.7e-4 0,0 0,0 -4.08616,67.57236 68.19576,55.52891 68.19576,55.52891 L 631.32988,483.371 c 0,0 5.39185,-6.90928 8.76198,-11.29046 3.37016,-4.38119 8.62719,-10.45503 8.62719,-10.45503 0,0 3.2512,-3.3504 4.92629,-5.07474 1.6713,-1.7205 6.72609,-7.35912 6.74903,-7.38476 -4e-5,2.5e-4 0,-9e-5 0,0.004 v 0.004 c 0.0582,0 0.57832,-0.45516 4.57729,-5.58507 4.87749,-6.25697 9.0658,-11.82463 9.0658,-11.82463 l 5.71474,-7.0946 c 0,0 3.30052,-3.99062 4.38444,-4.82816 1.08387,-0.83754 0.49331,-0.6407 2.56255,-1.72458 2.06922,-1.08389 5.20974,-2.07214 11.5029,-5.64394 6.29315,-3.57179 9.69472,-5.44282 13.94688,-9.0146 4.25209,-3.57179 6.65692,-6.62994 7.28662,-8.24916 0.62972,-1.61925 0.80981,-2.42907 0.80981,-3.59852 0,-1.16945 0.17947,-3.68829 0.0896,-4.49791 -0.0898,-0.80963 -0.8992,-4.76797 -1.16905,-5.30773 -0.26989,-0.53975 -1.2843,-3.1953 -2.0907,-4.40489 -0.80639,-1.20961 -1.8142,-2.89006 -3.6958,-4.97322 -1.88156,-2.08317 -4.67431,-4.5649 -6.9973,-5.66528 -2.32303,-1.10038 -5.16204,-2.54533 -8.16382,-3.01227 -3.0018,-0.46695 -5.87051,-0.93368 -9.93961,-0.86697 z"
            />
            {this.toolTip(
              "BACK_OF_THUMB",
              this.props.t("bodyMap.BACK_OF_LEFT_THUMB"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012056",
                  this.props.t("bodyMap.BACK_OF_LEFT_HAND")
                )
              }
              id="HAND_BACK_OF_LEFT_HAND"
              class="left_hand_frt_6"
              d="m 217.26012,441.60209 c 94.81637,-131.55772 232.30012,-77.0383 232.30012,-77.0383 0,0 67.55664,188.44753 26.07451,196.74398 -41.48217,8.29641 -178.96592,85.33472 -200.2996,40.29695 -21.33369,-45.03777 -58.07503,-160.00263 -58.07503,-160.00263 z"
            />
            {this.toolTip(
              "HAND_BACK_OF_LEFT_HAND",
              this.props.t("bodyMap.BACK_OF_LEFT_HAND"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyBRightHand() {
    return (
      <div className="flip" style={{ paddingTop: 130 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("fRightHand")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="768px"
            height="100%"
            viewBox="0 0 768 768"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="768"
              height="768"
              xlinkHref={require("../images/Back-Of-Hand(L) v2.jpg")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012077",
                  this.props.t("bodyMap.BACK_OF_RIGHT_PINKY")
                )
              }
              id="BACK_OF_PINKY"
              class="left_hand_frt_5"
              d="m 166.85664,419.04288 c 0,0 19.52686,-3.59116 30.74918,-12.56901 11.22235,-8.97786 12.12013,-19.3024 12.56903,-25.81138 0.4489,-6.50895 0.89778,-19.52683 -0.67333,-22.6691 -1.57112,-3.14226 -1.44799,-2.75186 -1.44799,-2.75186 l -4.73909,-1.13424 c 0,0 -4.99772,-1.2384 -6.32455,-1.72489 -1.32682,-0.48649 -7.78409,-3.22863 -7.78409,-3.22863 l -4.42277,-2.29981 -4.73235,-3.00749 -5.66115,-4.15742 -10.48195,-8.31481 -7.56295,-6.28032 -16.49692,-14.24131 -9.42047,-8.53596 -5.21889,-5.35153 -3.3613,-4.33432 -3.89202,-4.86504 -5.52847,-6.63414 c 0,0 -3.58243,-4.02473 -4.06895,-4.46701 -0.4865,-0.44228 -4.90925,-4.9535 -4.90925,-4.9535 l -8.37992,-7.46985 -7.109359,-4.94867 -6.412363,-3.6244 c 0,0 -4.948676,-1.95156 -6.412366,-2.36979 -1.463688,-0.41818 -3.903176,-0.76667 -3.903176,-0.76667 l -5.436566,-0.2091 c 0,0 -3.066787,0.2091 -4.669888,0.76669 -1.603083,0.55761 -4.600164,2.0213 -5.157761,2.50918 -0.557597,0.48789 -2.50919,1.9516 -2.50919,1.9516 l -1.46369,1.6031 -1.463703,2.43947 -1.115192,2.64859 -0.418188,2.78799 c 0,0 -0.209098,2.43948 -0.139408,3.13651 0.06971,0.69697 0.4182,3.13645 0.697006,3.76375 0.278791,0.62729 1.115193,3.06678 1.115193,3.06678 0,0 2.997081,6.20328 3.415282,6.96997 0.418187,0.7667 2.439487,3.69408 2.439487,3.69408 l 12.058034,16.10061 13.521722,17.77341 6.76086,8.64275 9.339757,11.98833 11.151939,14.91572 10.78422,13.93205 11.04145,12.51368 9.2538,9.14862 z"
            />
            {this.toolTip(
              "BACK_OF_PINKY",
              this.props.t("bodyMap.BACK_OF_RIGHT_PINKY"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012078",
                  this.props.t("bodyMap.BACK_OF_RIGHT_RING_FINGER")
                )
              }
              id="BACK_OF_RIGHT_RING_FINGER"
              class="left_hand_frt_4"
              d="m 226.15118,343.65935 c 54.98491,8.29961 68.67927,-38.80068 68.67927,-38.80068 l -3.38695,-4.54845 -6.98781,-11.21897 -8.65463,-15.45012 -7.18014,-13.46277 -9.61626,-20.51466 -5.44921,-11.41131 -3.39774,-7.693 -7.50067,-16.98872 -4.16707,-9.29572 -5.96207,-14.80904 c 0,0 -1.73092,-5.00045 -2.11558,-6.47493 -0.38464,-1.4745 -3.39774,-11.79595 -3.39774,-11.79595 l -3.46185,-11.79594 -3.97472,-12.88578 c 0,0 -3.52597,-10.57789 -3.91061,-11.28308 -0.38465,-0.70519 -2.43612,-6.0903 -2.43612,-6.0903 l -3.33363,-7.56479 -2.94899,-5.70566 c 0,0 -2.82076,-4.48759 -3.46184,-5.25687 -0.64109,-0.769305 -3.39776,-3.525975 -3.39776,-3.525975 l -2.24379,-1.730908 -2.62845,-1.73093 c 0,0 -2.24379,-0.89752 -3.20541,-1.282165 -0.96162,-0.384662 -4.29526,-1.153954 -4.29526,-1.153954 0,0 -3.14131,-0.128204 -4.10295,-0.192331 -0.96162,-0.0642 -5.32098,0.448756 -5.32098,0.448756 0,0 -2.11557,0.448764 -3.26954,1.025741 -1.15395,0.576977 -2.11558,0.89752 -3.26952,1.666811 -1.15395,0.769308 -2.24379,1.218072 -3.20541,2.2438 -0.96164,1.02573 -2.75667,3.20542 -2.949,3.718265 -0.19233,0.51289 -1.02574,2.05148 -1.21805,2.69257 -0.19233,0.64107 -0.6411,1.60269 -0.89752,2.75665 -0.25643,1.15396 -0.38465,3.20543 -0.38465,4.61582 0,1.41038 -0.19233,3.9106 -0.0642,6.41083 0.1282,2.50024 0.51286,7.11604 0.64107,8.52643 0.12821,1.41038 1.15396,8.97517 1.41041,11.41129 0.25642,2.43612 2.24378,12.50112 2.24378,12.50112 l 2.56434,13.01401 3.85933,15.73052 6.04563,23.9761 c 0,0 5.52326,21.87651 5.73987,23.01367 0.2166,1.13713 3.51973,13.48328 4.33198,16.62397 0.81225,3.1407 4.60273,17.00302 4.60273,17.00302 0,0 3.51973,12.6169 4.16954,14.99949 0.64978,2.3826 2.70747,8.60981 2.70747,8.60981 0,0 2.166,5.79404 2.59919,6.87702 0.43321,1.083 3.41145,7.74342 3.41145,7.74342 0,0 4.33198,9.31376 4.71102,10.39676 0.37905,1.083 3.89878,8.77226 4.38613,10.01769 0.48734,1.24545 3.51973,7.90588 3.95293,9.15132 0.43321,1.24545 3.03239,8.01416 3.03239,8.01416 l 1.7328,4.332 c 0,0 0.86639,3.14067 0.86639,3.79047 0,0.6498 0.10833,1.35149 0.10833,1.35149 z"            
            />
            {this.toolTip(
              "BACK_OF_RIGHT_RING_FINGER",
              this.props.t("bodyMap.BACK_OF_RIGHT_RING_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012076",
                  this.props.t("bodyMap.BACK_OF_RIGHT_MIDDLE_FINGER")
                )
              }
              id="BACK_OF_RIGHT_MIDDLE_FINGER"
              class="left_hand_frt_3"
              d="m 305.84655,302.65337 c 45.10107,20.56078 77.82145,-15.47586 77.82145,-15.47586 l -4.20058,-19.23428 -3.75843,-23.65596 -5.30601,-30.73062 -4.70807,-21.84737 -3.31644,-12.54482 -3.46064,-11.67967 -4.32581,-14.13094 -5.47935,-19.7545 -2.45128,-10.09353 -3.60484,-15.4287 -1.58612,-7.49805 -1.00936,-6.056125 -1.87451,-8.219021 -3.02806,-12.256436 c 0,0 -0.57678,-3.028063 -3.60483,-10.381918 -3.02806,-7.353869 -6.20033,-12.977411 -7.64225,-15.140307 -1.44194,-2.162899 -5.33516,-6.488699 -5.33516,-6.488699 l -4.46999,-4.03742 c 0,0 -2.59548,-1.73032 -4.3258,-2.59549 -1.73033,-0.86515 -3.74903,-1.29773 -6.20032,-1.58612 -2.45129,-0.28838 -6.77709,0.43258 -7.93064,0.57677 -1.15354,0.1442 -5.91192,1.58613 -6.4887,2.01871 -0.57677,0.43258 -2.88386,1.87452 -3.60483,2.3071 -0.72097,0.43258 -3.60484,3.31645 -4.47,4.90257 -0.86516,1.58613 -3.31645,6.777091 -3.60483,8.795801 -0.28838,2.018705 -0.86516,8.795791 -0.86516,10.526111 0,1.730326 0.43258,9.084188 0.43258,9.084188 l 2.88387,17.303203 2.88387,15.861263 2.88386,22.63837 3.17226,28.83867 1.87451,22.49417 2.88387,27.54094 4.3258,38.35543 4.1816,26.81997 c 0,0 2.16291,14.27514 2.16291,16.58224 0,2.30709 0.86516,8.79579 0.86516,9.94934 0,1.15354 0.28027,4.24099 0.28027,4.24099 z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_MIDDLE_FINGER",
              this.props.t("bodyMap.BACK_OF_RIGHT_MIDDLE_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012073",
                  this.props.t("bodyMap.BACK_OF_RIGHT_INDEX_FINGER")
                )
              }
              id="BACK_OF_RIGHT_INDEX_FINGER"
              class="left_hand_frt_2"
              d="m 411.3237,277.46487 c 26.54877,43.78218 76.118,6.06759 76.118,6.06759 l 1.84474,-7.8492 2.67271,-12.24165 3.43196,-18.04372 c 0,0 1.87014,-11.30208 2.60192,-20.08354 0.73182,-8.78148 1.30095,-20.73403 1.30095,-20.73403 l 0.81314,-16.83116 0.48786,-16.26198 0.56914,-30.73514 0.40656,-28.62109 0.32526,-9.10671 c 0,0 -0.16265,-4.228112 -0.48787,-6.667413 -0.32526,-2.439292 -1.13834,-7.643124 -1.38226,-8.374919 -0.24396,-0.731786 -2.2767,-6.260861 -2.60191,-6.667409 -0.32526,-0.406552 -1.95149,-3.740258 -3.33375,-5.285146 -1.38226,-1.544887 -2.84582,-3.496326 -5.36643,-4.797283 -2.5206,-1.300959 -3.17109,-1.788822 -5.61039,-2.357989 -2.43931,-0.569174 -3.17109,-1.057029 -6.34218,-1.057029 -3.17108,0 -3.98418,-0.406552 -6.58609,0.406554 -2.60191,0.813097 -3.3337,0.894408 -4.95991,2.114056 -1.62619,1.219647 -3.98419,3.415013 -3.98419,3.415013 0,0 -1.38225,1.138339 -2.76451,3.171089 -1.38231,2.032747 -3.00848,5.041214 -3.00848,5.041214 l -1.5449,3.496327 -2.05161,5.674964 -3.42951,13.718121 -2.28636,17.86213 -0.88973,11.60272 c 0,0 -0.2684,4.16039 -0.80522,7.15766 -0.53684,2.99727 -1.25261,8.7234 -1.87889,11.49699 -0.62631,2.77359 -0.71576,5.23404 -1.65522,9.17075 -0.93943,3.93671 -1.29732,6.21822 -1.92361,8.7234 -0.62631,2.50517 -2.14731,9.4839 -2.14731,9.4839 l -1.83414,8.8576 -2.54992,11.8996 -1.74469,8.63393 -1.69993,8.45499 z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_INDEX_FINGER",
              this.props.t("bodyMap.BACK_OF_RIGHT_INDEX_FINGER"),
              "top"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012080",
                  this.props.t("bodyMap.BACK_OF_RIGHT_THUMB")
                )
              }
              id="BACK_OF_THUMB"
              class="left_hand_frt_1"
              d="m 688.27301,362.88308 0.009,0.005 c -4.06912,0.0668 -9.20499,0.66685 -12.27348,1.26721 -3.06853,0.60035 -10.53961,2.00094 -15.14237,3.6019 -4.60277,1.60095 -13.14142,4.66948 -16.20993,6.20373 -3.06847,1.53425 -7.73813,4.06913 -10.80661,6.20374 -3.06851,2.13461 -7.00404,4.73646 -10.53949,8.00509 -3.53548,3.26863 -4.06885,3.60163 -5.93663,5.66954 -1.86777,2.06791 -3.93606,4.73701 -5.27018,6.6048 -1.33414,1.8678 -2.73472,4.26875 -3.2017,4.8691 -0.46691,0.60037 -0.66725,0.20049 -4.60289,3.33569 -3.9357,3.13522 -9.60595,7.20385 -9.60595,7.20385 l -6.00316,4.73598 c 0,0 -3.13533,2.60187 -7.33786,6.07061 -4.20251,3.46875 -6.7156,5.67315 -8.88316,7.59381 -2.16763,1.92065 -4.11562,3.53936 -6.2558,5.37769 -2.14015,1.83835 -3.21018,2.93631 -4.99365,4.36309 -1.78347,1.42678 -4.33518,3.70429 -6.06385,4.96642 -1.72857,1.26212 -2.05799,1.61883 -4.41767,3.21019 -2.35965,1.59145 -3.07329,2.2771 -5.29579,3.45686 -2.02192,1.07343 -4.65595,2.12402 -5.11486,2.30485 -0.0198,0.014 -0.0407,0.0271 -0.0622,0.0393 0,2.7e-4 0,0 0,0 -4.08616,67.57236 68.19576,55.52891 68.19576,55.52891 L 631.32988,483.371 c 0,0 5.39185,-6.90928 8.76198,-11.29046 3.37016,-4.38119 8.62719,-10.45503 8.62719,-10.45503 0,0 3.2512,-3.3504 4.92629,-5.07474 1.6713,-1.7205 6.72609,-7.35912 6.74903,-7.38476 -4e-5,2.5e-4 0,-9e-5 0,0.004 v 0.004 c 0.0582,0 0.57832,-0.45516 4.57729,-5.58507 4.87749,-6.25697 9.0658,-11.82463 9.0658,-11.82463 l 5.71474,-7.0946 c 0,0 3.30052,-3.99062 4.38444,-4.82816 1.08387,-0.83754 0.49331,-0.6407 2.56255,-1.72458 2.06922,-1.08389 5.20974,-2.07214 11.5029,-5.64394 6.29315,-3.57179 9.69472,-5.44282 13.94688,-9.0146 4.25209,-3.57179 6.65692,-6.62994 7.28662,-8.24916 0.62972,-1.61925 0.80981,-2.42907 0.80981,-3.59852 0,-1.16945 0.17947,-3.68829 0.0896,-4.49791 -0.0898,-0.80963 -0.8992,-4.76797 -1.16905,-5.30773 -0.26989,-0.53975 -1.2843,-3.1953 -2.0907,-4.40489 -0.80639,-1.20961 -1.8142,-2.89006 -3.6958,-4.97322 -1.88156,-2.08317 -4.67431,-4.5649 -6.9973,-5.66528 -2.32303,-1.10038 -5.16204,-2.54533 -8.16382,-3.01227 -3.0018,-0.46695 -5.87051,-0.93368 -9.93961,-0.86697 z"
            />
            {this.toolTip(
              "BACK_OF_THUMB",
              this.props.t("bodyMap.BACK_OF_RIGHT_THUMB"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012072",
                  this.props.t("bodyMap.BACK_OF_RIGHT_HAND")
                )
              }
              id="HAND_BACK_OF_RIGHT_HAND"
              class="left_hand_frt_6"
              d="m 217.26012,441.60209 c 94.81637,-131.55772 232.30012,-77.0383 232.30012,-77.0383 0,0 67.55664,188.44753 26.07451,196.74398 -41.48217,8.29641 -178.96592,85.33472 -200.2996,40.29695 -21.33369,-45.03777 -58.07503,-160.00263 -58.07503,-160.00263 z"
            />
            {this.toolTip(
              "HAND_BACK_OF_RIGHT_HAND",
              this.props.t("bodyMap.BACK_OF_RIGHT_HAND"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  frontRightUpperLimb() {
    return (
      <div style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("backLeftUpperLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div style={{}}>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="100%"
            viewBox="0 0 350 620"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "52" : null}
              y="0"
              width={this.state.isFirefox ? "70%" : "350"}
              height="600"
              xlinkHref={require("../images/Front-Upper-Arm(R) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012124",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_UPPER_ARM")
                )
              }
              id="FRONT_OF_UPPER_ARM"
              d="M 169 225 c -0.7349 -11.801 -11.7349 -53.801 -16.7349 -63.801 c -5 -9 -4 -8 -8 -19 C 148 82 120 92 86 93 c 30 74 9 94 1 116 c 3.3928 9.3538 13 32 14 36 S 145 249 168 225 z"
            />
            {this.toolTip(
              "FRONT_OF_UPPER_ARM",
              this.props.t("bodyMap.FRONT_OF_RIGHT_UPPER_ARM"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012171",
                  this.props.t("bodyMap.RIGHT_ARMPIT")
                )
              }
              id="RIGHT_ARMPIT"
              d="M 86 93 C 117 169 93 191 87 209 L 79 191 L 71 207 C 53 198 38 92 86 93"
            />
            {this.toolTip(
              "RIGHT_ARMPIT",
              this.props.t("bodyMap.RIGHT_ARMPIT"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012176",
                  this.props.t("bodyMap.RIGHT_ELBOW_PIT")
                )
              }
              id="ELBOW_PIT"
              d="M 168 225 C 159 238 120 252 101 245 C 102.3333 249.3333 102 253 105 258 S 103 269 112 306 C 130 289 168 280 193 285 C 187 261 176 243 169 225 Z"
            />
            {this.toolTip(
              "ELBOW_PIT",
              this.props.t("bodyMap.RIGHT_ELBOW_PIT"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012122",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_LOWER_ARM")
                )
              }
              id="FRONT_OF_LOWER_ARM"
              d="M 193 285 C 168 280 130 289 112 306 C 119 341 143 378 159 404 C 163 410 167 418 171 428 C 191 412 199 410 218 411 C 205 373 201 307 193 285 Z"
            />
            {this.toolTip(
              "FRONT_OF_LOWER_ARM",
              this.props.t("bodyMap.FRONT_OF_RIGHT_LOWER_ARM"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012125",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_WRIST")
                )
              }
              id="FRONT_OF_WRIST"
              d="M 218 411 C 199 410 191 412 171 428 C 175 436 178 446 182 456 C 194 446 209 439 226 435 C 224 427 221 419 218 411 Z"
            />
            {this.toolTip(
              "FRONT_OF_WRIST",
              this.props.t("bodyMap.FRONT_OF_RIGHT_WRIST"),
              "right"
            )}

            <path
              onClick={() => this.changeView("fRightHand")}
              id="PALM_SIDE_OF_RIGHT_HAND"
              d="M 226 435 C 209 439 194 446 182 456 C 179 488 186 517 187 520 C 192 537 198 563 202 570 C 208 576 211 569 211 567 C 213 560 206.3333 539.6667 204 526 C 203 524 205 521 207 526 C 211 545 214.3333 564 218 583 C 227 599 234 587 233 582 C 231 563 227 544.6667 224 526 C 224 525 225 522 227 526 C 232 546 234 567 238 587 C 239 595 252 600 253 586 C 252 562 247 541 242 520 C 241 517 244 516 245 520 C 248 537 253 552 256 572 C 263 585 271 577 271 574 C 271 551 266 528 262 506 C 260 498.3333 258 490.6667 256 483 C 257.3333 482.6667 253 479 260 482 C 263 483 266 484.6667 269 486 C 270 490 282 502 290 498 C 294 497 298 494 295 487 C 288 482 285 476 284 475 C 277 468 268 465 263 458 C 253 451 241 446 230 440 C 228.6667 438.3333 227 438 226 435 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_RIGHT_HAND",
              this.props.t("bodyMap.PALM_SIDE_OF_RIGHT_HAND"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  frontLeftUpperLimb() {
    return (
      <div className="flip" style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("backRightUpperLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="100%"
            viewBox="0 0 350 620"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "52" : null}
              width={this.state.isFirefox ? "70%" : "350"}
              height="600"
              xlinkHref={require("../images/Front-Upper-Arm(R) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012113",
                  this.props.t("bodyMap.FRONT_OF_LEFT_UPPER_ARM")
                )
              }
              id="FRONT_OF_UPPER_ARM"
              d="M 169 225 c -0.7349 -11.801 -11.7349 -53.801 -16.7349 -63.801 c -5 -9 -4 -8 -8 -19 C 148 82 120 92 86 93 c 30 74 9 94 1 116 c 3.3928 9.3538 13 32 14 36 S 145 249 168 225 z"
            />
            {this.toolTip(
              "FRONT_OF_UPPER_ARM",
              this.props.t("bodyMap.FRONT_OF_LEFT_UPPER_ARM"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012132",
                  this.props.t("bodyMap.LEFT_ARMPIT")
                )
              }
              id="LEFT_ARMPIT"
              d="M 86 93 C 117 169 93 191 87 209 L 79 191 L 71 207 C 53 198 38 92 86 93"
            />
            {this.toolTip(
              "LEFT_ARMPIT",
              this.props.t("bodyMap.LEFT_ARMPIT"),
              "bottom"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012137",
                  this.props.t("bodyMap.LEFT_ELBOW_PIT")
                )
              }
              id="ELBOW_PIT"
              d="M 168 225 C 159 238 120 252 101 245 C 102.3333 249.3333 102 253 105 258 S 103 269 112 306 C 130 289 168 280 193 285 C 187 261 176 243 169 225 Z"
            />
            {this.toolTip(
              "ELBOW_PIT",
              this.props.t("bodyMap.LEFT_ELBOW_PIT"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012111",
                  this.props.t("bodyMap.FRONT_OF_LEFT_LOWER_ARM")
                )
              }
              id="FRONT_OF_LOWER_ARM"
              d="M 193 285 C 168 280 130 289 112 306 C 119 341 143 378 159 404 C 163 410 167 418 171 428 C 191 412 199 410 218 411 C 205 373 201 307 193 285 Z"
            />
            {this.toolTip(
              "FRONT_OF_LOWER_ARM",
              this.props.t("bodyMap.FRONT_OF_LEFT_LOWER_ARM"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012114",
                  this.props.t("bodyMap.FRONT_OF_LEFT_WRIST")
                )
              }
              id="FRONT_OF_WRIST"
              d="M 218 411 C 199 410 191 412 171 428 C 175 436 178 446 182 456 C 194 446 209 439 226 435 C 224 427 221 419 218 411 Z"
            />
            {this.toolTip(
              "FRONT_OF_WRIST",
              this.props.t("bodyMap.FRONT_OF_LEFT_WRIST"),
              "left"
            )}

            <path
              onClick={() => this.changeView("fLeftHand")}
              id="PALM_SIDE_OF_LEFT_HAND"
              d="M 226 435 C 209 439 194 446 182 456 C 179 488 186 517 187 520 C 192 537 198 563 202 570 C 208 576 211 569 211 567 C 213 560 206.3333 539.6667 204 526 C 203 524 205 521 207 526 C 211 545 214.3333 564 218 583 C 227 599 234 587 233 582 C 231 563 227 544.6667 224 526 C 224 525 225 522 227 526 C 232 546 234 567 238 587 C 239 595 252 600 253 586 C 252 562 247 541 242 520 C 241 517 244 516 245 520 C 248 537 253 552 256 572 C 263 585 271 577 271 574 C 271 551 266 528 262 506 C 260 498.3333 258 490.6667 256 483 C 257.3333 482.6667 253 479 260 482 C 263 483 266 484.6667 269 486 C 270 490 282 502 290 498 C 294 497 298 494 295 487 C 288 482 285 476 284 475 C 277 468 268 465 263 458 C 253 451 241 446 230 440 C 228.6667 438.3333 227 438 226 435 Z"
            />
            {this.toolTip(
              "PALM_SIDE_OF_LEFT_HAND",
              this.props.t("bodyMap.PALM_SIDE_OF_LEFT_HAND"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  frontRightLowerLimb() {
    return (
      <div style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("backLeftLowerLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="90%"
            viewBox="0 0 200 380"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "60" : null}
              // width="200"
              width={this.state.isFirefox ? "40%" : "200"}
              height="370"
              xlinkHref={require("../images/Front-Lower-Limb(R) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012123",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_THIGH")
                )
              }
              id="FRONT_OF_RIGHT_THIGH"
              d="M 111.54 2.186 C 87.622 18.416 75.093 40.341 64.843 57.14 C 67.975 84.19 59.148 67.96 70.822 130.033 C 71.878 139.029 73.105 148.843 76.049 158.167 C 98.866 170.189 102.547 145.899 114.569 145.899 C 135.178 107.624 119.967 75.483 119.569 55.88 C 116.891 38.991 112.154 24.985 111.536 1.917 Z"
            />
            {this.toolTip(
              "FRONT_OF_RIGHT_THIGH",
              this.props.t("bodyMap.FRONT_OF_RIGHT_THIGH"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012120",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_KNEE")
                )
              }
              id="FRONT_OF_KNEE"
              d="M 114.527 145.896 C 102.59 145.896 98.849 170.126 76.045 158.367 C 78.182 165.137 77.292 176.361 79.964 180.459 C 78.361 186.694 78.6573 189.4857 78.004 193.999 C 87.268 199.522 104.015 205.401 112.923 190.97 C 110.963 177.787 112.032 158.546 114.527 145.896 Z"
            />
            {this.toolTip(
              "FRONT_OF_KNEE",
              this.props.t("bodyMap.FRONT_OF_RIGHT_KNEE"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012117",
                  this.props.t("bodyMap.FRONT_OF_RIGHT_CALF")
                )
              }
              id="FRONT_OF_CALF"
              d="M 112.92 190.915 C 103.956 205.446 87.377 199.46 78.013 193.933 C 77.399 203.604 75.25 215.118 76.171 222.946 C 81.697 241.828 82.141 233.27 84.767 262.245 C 85.754 270.015 87.728 277.963 87.728 285.555 L 86.725 290.855 C 92.312 288.706 101.05 288.134 107.496 290.998 C 108.454 254.489 117.502 213.876 113.078 190.956 Z"
            />
            {this.toolTip(
              "FRONT_OF_CALF",
              this.props.t("bodyMap.FRONT_OF_RIGHT_CALF"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012170",
                  this.props.t("bodyMap.RIGHT_ANKLE")
                )
              }
              id="RIGHT_ANKLE"
              d="M 107.48 290.997 C 101.058 288.092 92.343 288.703 86.686 290.844 C 84.359 297.306 85.495 302.176 86.063 304.854 C 85.09 307.37 83.953 310.454 84.684 315.08 C 91.826 312.97 100.347 309.886 109.031 309.805 C 108.057 307.694 107.651 304.61 107.976 299.498 C 108.788 297.956 108.544 296.819 108.138 294.79 C 107.9217 293.5187 107.7053 292.2473 107.489 291.057 Z"
            />
            {this.toolTip("RIGHT_ANKLE", this.props.t("bodyMap.RIGHT_ANKLE"), "right")}

            <path
              onClick={() => this.changeView("fRightFoot")}
              id="LEG_TOP_OF_RIGHT_FOOT"
              d="M 109.031 309.805 C 100.321 309.858 91.792 312.948 84.674 315.097 C 84.808 318.052 85.95 323.76 92.195 325.909 C 93.337 329.267 95.016 338.871 95.486 342.161 C 96.158 343.169 96.493 345.721 99.784 347.87 C 103.592 356.458 110.932 355.88 113.753 351.832 C 115.138 353.406 117.365 353.983 119.138 350.891 C 120.087 351.592 121.983 351.509 122.932 348.664 C 123.385 348.788 124.664 349.2 125.159 347.468 C 126.19 347.509 130.313 347.138 125.983 335.097 C 125.458 331.43 118.532 329.837 109.044 309.823 Z"
            />
            {this.toolTip(
              "LEG_TOP_OF_RIGHT_FOOT",
              this.props.t("bodyMap.TOP_OF_RIGHT_FOOT"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  frontLeftLowerLimb() {
    return (
      <div className="flip" style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("backRightLowerLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="90%"
            viewBox="0 0 200 380"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "60" : null}
              xlinkHref={require("../images/Front-Lower-Limb(R) v2.png")}
              width={this.state.isFirefox ? "40%" : "200"}
              height="370"
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012112",
                  this.props.t("bodyMap.FRONT_OF_LEFT_THIGH")
                )
              }
              id="FRONT_OF_LEFT_THIGH"
              d="M 111.54 2.186 C 87.622 18.416 75.093 40.341 64.843 57.14 C 67.975 84.19 59.148 67.96 70.822 130.033 C 71.878 139.029 73.105 148.843 76.049 158.167 C 98.866 170.189 102.547 145.899 114.569 145.899 C 135.178 107.624 119.967 75.483 119.569 55.88 C 116.891 38.991 112.154 24.985 111.536 1.917 Z"
            />
            {this.toolTip(
              "FRONT_OF_LEFT_THIGH",
              this.props.t("bodyMap.FRONT_OF_LEFT_THIGH"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012109",
                  this.props.t("bodyMap.FRONT_OF_LEFT_KNEE")
                )
              }
              id="FRONT_OF_KNEE"
              d="M 114.527 145.896 C 102.59 145.896 98.849 170.126 76.045 158.367 C 78.182 165.137 77.292 176.361 79.964 180.459 C 78.361 186.694 78.6573 189.4857 78.004 193.999 C 87.268 199.522 104.015 205.401 112.923 190.97 C 110.963 177.787 112.032 158.546 114.527 145.896 Z"
            />
            {this.toolTip(
              "FRONT_OF_KNEE",
              this.props.t("bodyMap.FRONT_OF_LEFT_KNEE"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012106",
                  this.props.t("bodyMap.FRONT_OF_LEFT_CALF")
                )
              }
              id="FRONT_OF_CALF"
              d="M 112.92 190.915 C 103.956 205.446 87.377 199.46 78.013 193.933 C 77.399 203.604 75.25 215.118 76.171 222.946 C 81.697 241.828 82.141 233.27 84.767 262.245 C 85.754 270.015 87.728 277.963 87.728 285.555 L 86.725 290.855 C 92.312 288.706 101.05 288.134 107.496 290.998 C 108.454 254.489 117.502 213.876 113.078 190.956 Z"
            />
            {this.toolTip(
              "FRONT_OF_CALF",
              this.props.t("bodyMap.FRONT_OF_LEFT_CALF"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012131",
                  this.props.t("bodyMap.LEFT_ANKLE")
                )
              }
              id="LEFT_ANKLE"
              d="M 107.48 290.997 C 101.058 288.092 92.343 288.703 86.686 290.844 C 84.359 297.306 85.495 302.176 86.063 304.854 C 85.09 307.37 83.953 310.454 84.684 315.08 C 91.826 312.97 100.347 309.886 109.031 309.805 C 108.057 307.694 107.651 304.61 107.976 299.498 C 108.788 297.956 108.544 296.819 108.138 294.79 C 107.9217 293.5187 107.7053 292.2473 107.489 291.057 Z"
            />
            {this.toolTip("LEFT_ANKLE", this.props.t("bodyMap.LEFT_ANKLE"), "left")}

            <path
              onClick={() => this.changeView("fLeftFoot")}
              id="LEG_TOP_OF_LEFT_FOOT"
              d="M 109.031 309.805 C 100.321 309.858 91.792 312.948 84.674 315.097 C 84.808 318.052 85.95 323.76 92.195 325.909 C 93.337 329.267 95.016 338.871 95.486 342.161 C 96.158 343.169 96.493 345.721 99.784 347.87 C 103.592 356.458 110.932 355.88 113.753 351.832 C 115.138 353.406 117.365 353.983 119.138 350.891 C 120.087 351.592 121.983 351.509 122.932 348.664 C 123.385 348.788 124.664 349.2 125.159 347.468 C 126.19 347.509 130.313 347.138 125.983 335.097 C 125.458 331.43 118.532 329.837 109.044 309.823 Z"
            />
            {this.toolTip(
              "LEG_TOP_OF_LEFT_FOOT",
              this.props.t("bodyMap.TOP_OF_LEFT_FOOT"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  backLeftUpperLimb() {
    return (
      <div style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("frontRightUpperLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="100%"
            viewBox="0 0 350 620"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "60" : null}
              width={this.state.isFirefox ? "65%" : "350"}
              height="600"
              xlinkHref={require("../images/Back-Upper-Arm(R) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012081",
                  this.props.t("bodyMap.BACK_OF_RIGHT_UPPER_ARM")
                )
              }
              id="BACK_OF_RIGHT_UPPER_ARM"
              d="M 95.843 93.194 L 146.701 71.156 C 151.787 74.546 150.939 108.452 145.853 130.49 C 158.568 161.005 162.806 172.872 168.727 216.337 C 143.693 241.371 111.149 240.536 96.128 227.185 L 81.108 187.965 L 94.459 93.669 Z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_UPPER_ARM",
              this.props.t("bodyMap.BACK_OF_RIGHT_UPPER_ARM"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012175",
                  this.props.t("bodyMap.RIGHT_ELBOW")
                )
              }
              id="ELBOW"
              d="M 168.669 216.608 C 143.536 241.741 111.443 240.581 95.976 227.435 C 108.458 259.784 99.321 243.794 105.317 271.776 C 115.311 297.188 158.996 294.333 184.979 266.066 C 178.697 247.221 173.558 238.369 168.704 216.384 Z"
            />
            {this.toolTip(
              "ELBOW",
              this.props.t("bodyMap.RIGHT_ELBOW"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012075",
                  this.props.t("bodyMap.BACK_OF_RIGHT_LOWER_ARM")
                )
              }
              id="BACK_OF_LOWER_ARM"
              d="M 184.979 266.065 C 158.996 294.332 115.311 297.187 105.317 271.776 C 112.208 344.17 126.783 352.499 157.808 414.466 C 169.028 414.466 188.072 411.808 203.278 402.36 C 190.981 341.741 200.004 323.696 185.082 265.745 Z"
            />
            {this.toolTip(
              "BACK_OF_LOWER_ARM",
              this.props.t("bodyMap.BACK_OF_RIGHT_LOWER_ARM"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012082",
                  this.props.t("bodyMap.BACK_OF_RIGHT_WRIST")
                )
              }
              id="BACK_OF_WRIST"
              d="M 203.383 402.278 C 188.121 411.858 169.071 414.421 157.819 414.421 C 163.389 430.017 165.729 438.484 166.397 446.059 C 180.768 444.945 199.595 437.815 209.956 429.794 C 208.545 424.001 207.616 417.874 203.272 402.389 Z"
            />
            {this.toolTip(
              "BACK_OF_WRIST",
              this.props.t("bodyMap.BACK_OF_RIGHT_WRIST"),
              "right"
            )}

            <path
              onClick={() => this.changeView("bLeftHand")}
              id="ARM_BACK_OF_RIGHT_HAND"
              d="M 210.057 429.755 C 199.469 437.761 180.875 444.992 166.285 446.025 C 159.886 459.528 162.653 470.094 163.031 476.132 C 164.322 485.626 163.95 494.54 168.036 503.825 C 170.632 517.009 172.312 530.095 175.407 542.386 C 176.468 548.045 185.428 551.512 186.106 540.175 C 185.916 529.458 184.819 518.249 182.748 506.918 C 182.504 506.065 183.235 502.531 185.185 506.918 C 188.359 520.459 190.125 542.53 192.884 558.089 C 193.546 563.938 203.808 569.786 206.236 556.544 C 205.464 540.322 205.133 525.645 201.712 507.548 C 201.271 505.892 202.264 503.795 203.478 507.437 C 209.216 543.301 207.671 551.246 209.768 559.743 C 211.092 567.689 222.017 570.889 222.679 558.971 C 223.341 546.17 223.562 526.417 220.031 505.671 C 219.955 504.974 220.302 502.107 221.432 505.669 C 223.951 519.396 225.428 534.86 226.644 547.283 C 230.068 556.281 238.284 555.583 240.067 546.825 C 239.205 521.789 239.615 496.131 232.431 469.652 C 231.662 468.2 230.407 462.314 234.279 468.626 C 236.39 472.542 246.811 479.683 251.539 474.086 C 254.986 471.509 252.158 465.247 248.771 462.253 C 247.215 456.511 243.577 453.897 240.167 451.736 C 236.756 448.781 233.005 445.37 231.072 441.846 C 224.478 436.958 216.975 432.979 210.04 429.796 Z"
            />
            {this.toolTip(
              "ARM_BACK_OF_RIGHT_HAND",
              this.props.t("bodyMap.BACK_OF_RIGHT_HAND"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  backRightUpperLimb() {
    return (
      <div className="flip" style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("frontLeftUpperLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="90%"
            viewBox="0 0 350 620"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "60" : null}
              width={this.state.isFirefox ? "65%" : "350"}
              height="600"
              xlinkHref={require("../images/Back-Upper-Arm(R) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012065",
                  this.props.t("bodyMap.BACK_OF_LEFT_UPPER_ARM")
                )
              }
              id="BACK_OF_LEFT_UPPER_ARM"
              d="M 95.843 93.194 L 146.701 71.156 C 151.787 74.546 150.939 108.452 145.853 130.49 C 158.568 161.005 162.806 172.872 168.727 216.337 C 143.693 241.371 111.149 240.536 96.128 227.185 L 81.108 187.965 L 94.459 93.669 Z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_UPPER_ARM",
              this.props.t("bodyMap.BACK_OF_LEFT_UPPER_ARM"),
              "left"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012136",
                  this.props.t("bodyMap.LEFT_ELBOW")
                )
              }
              id="ELBOW"
              d="M 168.669 216.608 C 143.536 241.741 111.443 240.581 95.976 227.435 C 108.458 259.784 99.321 243.794 105.317 271.776 C 115.311 297.188 158.996 294.333 184.979 266.066 C 178.697 247.221 173.558 238.369 168.704 216.384 Z"
            />
            {this.toolTip("ELBOW", this.props.t("bodyMap.LEFT_ELBOW"), "left")}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012059",
                  this.props.t("bodyMap.BACK_OF_LEFT_LOWER_ARM")
                )
              }
              id="BACK_OF_LOWER_ARM"
              d="M 184.979 266.065 C 158.996 294.332 115.311 297.187 105.317 271.776 C 112.208 344.17 126.783 352.499 157.808 414.466 C 169.028 414.466 188.072 411.808 203.278 402.36 C 190.981 341.741 200.004 323.696 185.082 265.745 Z"
            />
            {this.toolTip(
              "BACK_OF_LOWER_ARM",
              this.props.t("bodyMap.BACK_OF_LEFT_LOWER_ARM"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012066",
                  this.props.t("bodyMap.BACK_OF_LEFT_WRIST")
                )
              }
              id="BACK_OF_WRIST"
              d="M 203.383 402.278 C 188.121 411.858 169.071 414.421 157.819 414.421 C 163.389 430.017 165.729 438.484 166.397 446.059 C 180.768 444.945 199.595 437.815 209.956 429.794 C 208.545 424.001 207.616 417.874 203.272 402.389 Z"
            />
            {this.toolTip(
              "BACK_OF_WRIST",
              this.props.t("bodyMap.BACK_OF_LEFT_WRIST"),
              "left"
            )}

            <path
              onClick={() => this.changeView("bRightHand")}
              id="ARM_BACK_OF_LEFT_HAND"
              d="M 210.057 429.755 C 199.469 437.761 180.875 444.992 166.285 446.025 C 159.886 459.528 162.653 470.094 163.031 476.132 C 164.322 485.626 163.95 494.54 168.036 503.825 C 170.632 517.009 172.312 530.095 175.407 542.386 C 176.468 548.045 185.428 551.512 186.106 540.175 C 185.916 529.458 184.819 518.249 182.748 506.918 C 182.504 506.065 183.235 502.531 185.185 506.918 C 188.359 520.459 190.125 542.53 192.884 558.089 C 193.546 563.938 203.808 569.786 206.236 556.544 C 205.464 540.322 205.133 525.645 201.712 507.548 C 201.271 505.892 202.264 503.795 203.478 507.437 C 209.216 543.301 207.671 551.246 209.768 559.743 C 211.092 567.689 222.017 570.889 222.679 558.971 C 223.341 546.17 223.562 526.417 220.031 505.671 C 219.955 504.974 220.302 502.107 221.432 505.669 C 223.951 519.396 225.428 534.86 226.644 547.283 C 230.068 556.281 238.284 555.583 240.067 546.825 C 239.205 521.789 239.615 496.131 232.431 469.652 C 231.662 468.2 230.407 462.314 234.279 468.626 C 236.39 472.542 246.811 479.683 251.539 474.086 C 254.986 471.509 252.158 465.247 248.771 462.253 C 247.215 456.511 243.577 453.897 240.167 451.736 C 236.756 448.781 233.005 445.37 231.072 441.846 C 224.478 436.958 216.975 432.979 210.04 429.796 Z"
            />
            {this.toolTip(
              "ARM_BACK_OF_LEFT_HAND",
              this.props.t("bodyMap.BACK_OF_LEFT_HAND"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  backLeftLowerLimb() {
    return (
      <div className="flip" style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("frontRightLowerLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="350"
            height="90%"
            viewBox="0 0 200 380"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "50" : null}
              width={this.state.isFirefox ? "50%" : "200"}
              height="370"
              xlinkHref={require("../images/Back-Lower-Limb(L) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012079",
                  this.props.t("bodyMap.BACK_OF_RIGHT_THIGH")
                )
              }
              id="BACK_OF_RIGHT_THIGH"
              d="M 72.565 5.406 C 95.3327 6.1787 118.6313 6.8703 141.93 7.562 C 146.957 45.336 135.936 93.021 130.872 123.887 C 113.269 131.122 89.637 131.122 81.198 123.646 C 80.474 116.653 79.027 108.696 76.383 101.541 C 61.825 73.008 69.853 20 72.565 5.299 Z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_THIGH",
              this.props.t("bodyMap.BACK_OF_RIGHT_THIGH"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012180",
                  this.props.t("bodyMap.RIGHT_KNEE_PIT")
                )
              }
              id="RIGHT_KNEE_PIT"
              d="M 130.787 123.977 C 113.186 131.163 89.665 131.02 81.161 123.701 C 81.2837 136.6443 82.704 151.371 81.529 162.531 C 90.046 168.698 106.345 171.488 123.966 165.174 L 123.232 160.622 C 127.784 150.931 126.316 150.931 128.078 136.981 C 128.551 132.385 129.84 128.3657 130.721 124.058 Z"
            />
            {this.toolTip(
              "RIGHT_KNEE_PIT",
              this.props.t("bodyMap.RIGHT_KNEE_PIT"),
              "right"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012069",
                  this.props.t("bodyMap.BACK_OF_RIGHT_CALF")
                )
              }
              id="BACK_OF_RIGHT_CALF"
              d="M 123.982 165.201 C 106.233 171.495 90.121 168.726 81.561 162.558 C 81.093 183.0567 74.816 189.744 80.157 224.054 C 85.025 260.437 87.287 305.28 86.242 317.031 C 95.104 314.537 106.212 314.125 116.086 318.856 C 118.348 308.571 114.9887 303.4973 114.44 295.818 C 114.234 290.675 119.582 262.906 120.611 246.655 C 120.403 244.551 120.812 240.665 125.108 233.096 C 129.838 222.971 129.09 213.064 128.716 203.343 C 127.408 189.324 126.286 177.36 124.043 165.21 Z"
            />
            {this.toolTip(
              "BACK_OF_RIGHT_CALF",
              this.props.t("bodyMap.BACK_OF_RIGHT_CALF"),
              "right"
            )}

            <path
              onClick={() => this.changeView("bLeftFoot")}
              id="LEG_BOTTOM_OF_RIGHT_FOOT"
              d="M 117.117 340.071 C 117.956 360.04 100.672 352.824 99.33 352.824 C 93.182 351.085 89.109 347.584 86.744 344.602 C 75.032 342.551 68.347 339.175 68.774 333.284 C 67.288 330.901 68.148 326.797 72.12 326.333 C 72.385 324.877 74.304 323.024 76.687 322.759 C 78.144 321.766 80.924 317.861 82.777 319.714 C 83.505 318.787 84.366 317.662 86.02 317.662 C 87.653 323.9283 89.286 330.1947 90.919 336.461 C 94.493 340.035 106.54 344.735 117.131 340.102 Z"
            />
            {this.toolTip(
              "LEG_BOTTOM_OF_RIGHT_FOOT",
              this.props.t("bodyMap.BOTTOM_OF_RIGHT_FOOT"),
              "right"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  backRightLowerLimb() {
    return (
      <div style={{ paddingTop: 180 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            position: "relative",
            cursor: "pointer",
          }}
          onClick={() => this.changeView("frontLeftLowerLimb")}
        >
          <div
            style={{
              fontSize: "2rem",
              color: "#007bff",
              position: "absolute",
              top: "0rem",
              left: "calc(50% + 150px)",
            }}
          >
            <img
              src={require("../images/rotate.png")}
              style={{ width: 35, height: 35 }}
            />
          </div>
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="370"
            height="90%"
            viewBox="0 0 200 380"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              x={this.state.isFirefox ? "50" : null}
              width={this.state.isFirefox ? "50%" : "200"}
              height="370"
              xlinkHref={require("../images/Back-Lower-Limb(L) v2.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012063",
                  this.props.t("bodyMap.BACK_OF_LEFT_THIGH")
                )
              }
              id="BACK_OF_LEFT_THIGH"
              d="M 72.565 5.406 C 95.3327 6.1787 118.6313 6.8703 141.93 7.562 C 146.957 45.336 135.936 93.021 130.872 123.887 C 113.269 131.122 89.637 131.122 81.198 123.646 C 80.474 116.653 79.027 108.696 76.383 101.541 C 61.825 73.008 69.853 20 72.565 5.299 Z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_THIGH",
              this.props.t("bodyMap.BACK_OF_LEFT_THIGH"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012141",
                  this.props.t("bodyMap.LEFT_KNEE_PIT")
                )
              }
              id="LEFT_KNEE_PIT"
              d="M 130.787 123.977 C 113.186 131.163 89.665 131.02 81.161 123.701 C 81.2837 136.6443 82.704 151.371 81.529 162.531 C 90.046 168.698 106.345 171.488 123.966 165.174 L 123.232 160.622 C 127.784 150.931 126.316 150.931 128.078 136.981 C 128.551 132.385 129.84 128.3657 130.721 124.058 Z"
            />
            {this.toolTip(
              "LEFT_KNEE_PIT",
              this.props.t("bodyMap.LEFT_KNEE_PIT"),
              "left"
            )}

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012053",
                  this.props.t("bodyMap.BACK_OF_LEFT_CALF")
                )
              }
              id="BACK_OF_LEFT_CALF"
              d="M 123.982 165.201 C 106.233 171.495 90.121 168.726 81.561 162.558 C 81.093 183.0567 74.816 189.744 80.157 224.054 C 85.025 260.437 87.287 305.28 86.242 317.031 C 95.104 314.537 106.212 314.125 116.086 318.856 C 118.348 308.571 114.9887 303.4973 114.44 295.818 C 114.234 290.675 119.582 262.906 120.611 246.655 C 120.403 244.551 120.812 240.665 125.108 233.096 C 129.838 222.971 129.09 213.064 128.716 203.343 C 127.408 189.324 126.286 177.36 124.043 165.21 Z"
            />
            {this.toolTip(
              "BACK_OF_LEFT_CALF",
              this.props.t("bodyMap.BACK_OF_LEFT_CALF"),
              "left"
            )}

            <path
              onClick={() => this.changeView("bRightFoot")}
              id="LEG_BOTTOM_OF_LEFT_FOOT"
              d="M 117.117 340.071 C 117.956 360.04 100.672 352.824 99.33 352.824 C 93.182 351.085 89.109 347.584 86.744 344.602 C 75.032 342.551 68.347 339.175 68.774 333.284 C 67.288 330.901 68.148 326.797 72.12 326.333 C 72.385 324.877 74.304 323.024 76.687 322.759 C 78.144 321.766 80.924 317.861 82.777 319.714 C 83.505 318.787 84.366 317.662 86.02 317.662 C 87.653 323.9283 89.286 330.1947 90.919 336.461 C 94.493 340.035 106.54 344.735 117.131 340.102 Z"
            />
            {this.toolTip(
              "LEG_BOTTOM_OF_LEFT_FOOT",
              this.props.t("bodyMap.BOTTOM_OF_LEFT_FOOT"),
              "left"
            )}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  bodyPelvis() {
    return (
      <div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "19rem",
            right: "5rem",
            width: "50px",
          }}
        >
          {this.props.t("bodyMap.LEFT_INDICATOR_TEXT")}
        </div>
        <div
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
            color: "#007bff",
            position: "relative",
            top: "14.5rem",
            left: "22.5rem",
          }}
        >
          {this.props.t("bodyMap.RIGHT_INDICATOR_TEXT")}
        </div>
        <div>
          <svg
            ref={this.svgRef}
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="740"
            height="100%"
            viewBox="0 0 740 810"
            xmlSpace="preserve"
          >
            <image
              overflow="visible"
              width="740"
              y={this.state.isFirefox ? "55" : null}
              height={this.state.isFirefox ? "85%" : "800"}
              xlinkHref={require("../images/Pelvis v1.png")}
            ></image>

            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012179",
                  this.props.t("bodyMap.RIGHT_HIP")
                )
              }
              id="RIGHT_HIP"
              d="M 633 230.281 C 613.056 264.185 569.531 302.688 536.965 300.25 V 533.705 H 689.225 C 677.507 483.484 667.462 425.729 663.277 366.301 C 665.788 283.436 643.189 275.345 633.145 229.867 Z"
            />
            {this.toolTip("RIGHT_HIP", this.props.t("bodyMap.RIGHT_HIP"), "right")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012140",
                  this.props.t("bodyMap.LEFT_HIP")
                )
              }
              id="LEFT_HIP"
              d="M 111.357 231.688 C 117.425 249.89 154.695 288.027 210.167 302.762 V 531.585 H 59.352 C 77.554 458.778 77.2647 411.973 86.221 352.167 C 80.154 297.561 102.9783 271.8477 111.357 231.688 Z"
            />
            {this.toolTip("LEFT_HIP", this.props.t("bodyMap.LEFT_HIP"), "left")}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012246",
                  this.props.t("bodyMap.PELVIS_MID")
                )
              }
              id="PELVIS_MID"
              d="M 536.934 300.162 C 428.589 281.96 311.578 278.493 210.167 302.762 V 456.177 C 313.311 425.841 425.989 426.708 536.067 456.177 Z"
            />
            {this.toolTip(
              "PELVIS_MID",
              this.props.t("bodyMap.PELVIS_MID"),
              "top"
            )}
            <path
              onClick={() =>
                this.onBodyClick(
                  "20000012236",
                  this.props.t("bodyMap.GENITALIA")
                )
              }
              id="GENITALIA"
              d="M 507.888 449.478 C 454.675 435.62 329.958 424.534 251.248 446.706 C 273.42 497.702 317.209 553.131 358.227 604.681 H 389.397 C 434.547 557.24 476.036 506.803 507.763 449.451 Z"
            />
            {this.toolTip("GENITALIA", this.props.t("bodyMap.GENITALIA"), "bottom")}
          </svg>
          { this.renderDebugOverlay() }
        </div>
      </div>
    );
  }
  render() {
    const { isLoading } = this.props;

    return (
      <div style={{}} className="vPage bodyMapPage flex">
        <Loading isLoading={isLoading} />
        {this.state.form ? (
          this.bodyForm()
        ) : (
          <div className="table-container">
            <div className="table-cell" style={{ height: "85vh" }}>
              <div id="organswrapper">
                <div
                  style={{
                    height: "78%",
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {this.state.body === "front" && this.bodyFront()}
                  {this.state.body === "back" && this.bodyBack()}
                  {this.state.body === "head" && this.bodyHead()}
                  {this.state.body === "bHead" && this.bodyBhead()}
                  {this.state.body === "mouth" && this.bodyMouth()}
                  {this.state.body === "mouthCavity" && this.bodyMouthCavity()}
                  {this.state.body === "rightEye" && this.bodyRightEye()}
                  {this.state.body === "leftEye" && this.bodyLeftEye()}
                  {this.state.body === "rightEar" && this.bodyRightEar()}
                  {this.state.body === "leftEar" && this.bodyLeftEar()}
                  {this.state.body === "fRightHand" && this.bodyFrightHand()}
                  {this.state.body === "fLeftHand" && this.bodyFleftHand()}
                  {this.state.body === "fRightFoot" && this.bodyFrightFoot()}
                  {this.state.body === "fLeftFoot" && this.bodyFleftFoot()}
                  {this.state.body === "bRightFoot" && this.bodyBrightFoot()}
                  {this.state.body === "bLeftFoot" && this.bodyBleftFoot()}
                  {this.state.body === "bLeftHand" && this.bodyBRightHand()}
                  {this.state.body === "bRightHand" && this.bodyBLeftHand()}
                  {this.state.body === "pelvis" && this.bodyPelvis()}
                  {this.state.body === "frontRightUpperLimb" &&
                    this.frontRightUpperLimb()}
                  {this.state.body === "frontRightLowerLimb" &&
                    this.frontRightLowerLimb()}
                  {this.state.body === "frontLeftUpperLimb" &&
                    this.frontLeftUpperLimb()}
                  {this.state.body === "frontLeftLowerLimb" &&
                    this.frontLeftLowerLimb()}
                  {this.state.body === "backLeftUpperLimb" &&
                    this.backLeftUpperLimb()}
                  {this.state.body === "backRightUpperLimb" &&
                    this.backRightUpperLimb()}
                  {this.state.body === "backLeftLowerLimb" &&
                    this.backLeftLowerLimb()}
                  {this.state.body === "backRightLowerLimb" &&
                    this.backRightLowerLimb()}
                </div>
              </div>
              <div
                className="buttons row formRow "
                style={{
                  position: "relative",
                  width: "100%",
                  bottom: 0,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent:
                    this.state.body != "front" && this.state.body != "back"
                      ? "space-between"
                      : "center",
                }}
              >
                {this.state.body != "front" && this.state.body != "back" ? (
                  <button
                    style={{ margin: 0, width: "49%" }}
                    onClick={() => {
                      this.changeView(this.state.bodyBack);
                    }}
                    className="btn btn-primary cancel center"
                  >
                    {this.props.t("bodyMap.BACK_BUTTON")}
                  </button>
                ) : null}
                <button
                  style={{
                    margin: 0,
                    width:
                      this.state.body != "front" && this.state.body != "back"
                        ? "49%"
                        : "65%",
                  }}
                  onClick={() => {
                    localStorage.setItem("bodyMapSymptom", "cancel");
                    let returnPage =
                      localStorage.getItem("bodyreturn") || "symptom";
                    sessionStorage.removeItem("previousPageIsDirty"); // ✅ Clear session flag
                    window.location = "/" + returnPage;
                  }}
                  className="btn btn-primary cancel center"
                >
                  {this.props.t("general.CANCEL")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

BodyMap = translate("translations")(BodyMap);

const mapStateToProps = (state) => ({ ...state.bodymap });

const matchDispatchToProps = (dispatch) =>
  bindActionCreators({ ...bodyMapActions }, dispatch);

export default connect(mapStateToProps, matchDispatchToProps)(BodyMap);



// WEBPACK FOOTER //
// ./src/pages/BodyMap.js